import React, { useEffect, useMemo, useState } from "react";
// eslint-disable-next-line no-unused-vars
import { parentData } from './parentDataImport';
import { supabase, getUserPreferences, saveUserPreferences } from './supabaseClient';

/* =========================================================
   EMAIL CONFIGURATION
   ========================================================= */

// Email is sent through /api/send-email, a serverless function that holds the
// Brevo key and the From address server-side. Nothing about the mail provider
// belongs in the browser bundle.

// Helper function to send verification code via Brevo
async function sendVerificationCode(toEmail, userName, verificationCode, isSignup = false) {
  try {
    console.log(`📧 Sending verification email to ${toEmail}...`);

    const subject = isSignup ? 'Email Verification Code' : 'Password Reset Verification Code';
    const heading = isSignup ? 'Email Verification' : 'Password Reset';
    const message = isSignup
      ? 'Thank you for signing up! Please verify your email address using the code below:'
      : 'You requested to reset your password. Use the verification code below:';
    const footer = isSignup
      ? 'If you didn\'t sign up for MAPS Sunday School, please ignore this email.'
      : 'If you didn\'t request a password reset, please ignore this email.';

    // Brevo API call
    const response = await fetch('/api/send-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to: toEmail,
        toName: userName,
        subject: subject,
        htmlContent: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2 style="color: #7c1d1d;">MAPS Sunday School - ${heading}</h2>
            <p>Hello ${userName},</p>
            <p>${message}</p>
            <div style="background-color: #f5f5f5; padding: 20px; text-align: center; margin: 20px 0; border-radius: 8px;">
              <h1 style="color: #7c1d1d; font-size: 36px; letter-spacing: 8px; margin: 0;">${verificationCode}</h1>
            </div>
            <p>This code is valid for this session only. ${footer}</p>
            <p style="color: #666; font-size: 12px; margin-top: 30px;">
              MAPS Sunday School Logistics Management System
            </p>
          </div>
        `
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ Email service error:', response.status, errorData);
      // Carry the server's explanation up so the caller can show something
      // truthful instead of blaming the address the person typed.
      const err = new Error(errorData.error || `Email service returned ${response.status}`);
      err.serverMessage = errorData.error || '';
      throw err;
    }

    console.log('✅ Verification email sent successfully');
    return { success: true };
  } catch (error) {
    console.error('❌ Failed to send verification email:', error);
    return {
      success: false,
      error: error.message || error.toString(),
      serverMessage: error.serverMessage || '',
    };
  }
}

/* =========================================================
   CONSTANTS & HELPERS
   ========================================================= */

// Current school year. Students and attendance rows are stamped with this, and
// every destructive write in saveStateToSupabase is scoped to it, so prior
// years stay out of reach of the app's save path. Bump this at rollover.
const CURRENT_SCHOOL_YEAR = "2026-27";

// Approvals is deliberately narrower than admin: it is the queue for whoever
// vets new teacher signups, not something every administrator works. Add an
// address here to grant access. Compared lowercased, like login.
const APPROVALS_EMAILS = [
  "haya.awadallah1@gmail.com",
  "awadallahyasseen@gmail.com",
];

function canApproveUsers(user) {
  return !!user && APPROVALS_EMAILS.includes((user.email || "").trim().toLowerCase());
}

const SESSIONS = ["AM", "PM"];
const GRADES_AM = [
  "Pre-K",
  "KG A",
  "KG B",
  "Grade 1A",
  "Grade 1B",
  "Grade 2A",
  "Grade 2B",
  "Grade 3A",
  "Grade 3B",
  "Grade 4A",
  "Grade 4B",
  "Grade 5A",
  "Grade 5B",
  "Grade 6A",
  "Grade 6B",
  // 7th and 8th are taught as two combined classes split by gender, not as
  // separate grades. See the 2026-27 Teacher & Class Assignments sheet.
  "7/8 Boys",
  "7/8 Girls",
  "Grade 9/10",
];

const GRADES_PM = [
  "Pre-K PM",
  "KG PM",
  "Grade 1 PM",
  "Grade 2 PM",
  "Grade 3 PM",
  "Grade 4 PM",
  "Grade 5/6 PM",
  "Muhsen",
  "Online",
];

const ALL_GRADES = [...GRADES_AM, ...GRADES_PM];

const TEAM_LEAD_GRADES = [
  "PreK",
  "Kindergarten",
  "1st",
  "2nd",
  "3rd",
  "4th",
  "5th",
  "6th",
  "7th",
  "8th",
  "9th",
  "10th"
];

// Seats per class, from the room each one occupies. Mirrors
// scripts/roomCapacity.json. Classes above their seat count are shown as
// "overflow" rather than blocked: the roster is the roster, and the room is
// a scheduling problem for a human to solve. Grade 1B is absent because the
// assignments sheet gives its room only as "157", with no seat count.
const ROOM_CAPACITY = {
  "Grade 2B": 22,
  "Pre-K": 20,
  "Grade 6B": 20,
  "7/8 Boys": 20,
  "KG B": 15,
  "Grade 3A": 15,
  "Grade 3B": 15,
  "Grade 4A": 15,
  "Grade 4B": 15,
  "Grade 5A": 15,
  "Grade 5B": 15,
  "Grade 1A": 14,
  "Grade 6A": 14,
  "7/8 Girls": 14,
  "Grade 2A": 12,
  "Grade 9/10": 10,
  "KG A": 8,
};

// Teacher, assistant and room by class.
// Source: "Teacher & Class Assignments" sheet, SHAYMA SS 2026-2027.
// Muhsen is left out because the sheet leaves its row blank.
const GRADE_STAFF = {
  // AM (A) classes
  "Pre-K": {
    room: "112",
    lead: "Kelley and Razan",
    ta: "Armin"
  },
  "KG A": {
    room: "201",
    lead: "Hafsa",
    ta: "Sama"
  },
  "Grade 1A": {
    room: "301",
    lead: "Maryam Al Shammari",
    ta: "Salma"
  },
  "Grade 2A": {
    room: "157B",
    lead: "Fajr",
    ta: "Maryam Ibrahim"
  },
  "Grade 3A": {
    room: "200",
    lead: "Musfira",
    ta: "Bayan"
  },
  "Grade 4A": {
    room: "150B",
    lead: "Hammad",
    ta: "Hamza"
  },
  "Grade 5A": {
    room: "306A",
    lead: "Shamima",
    ta: "Minhah"
  },
  "Grade 6A": {
    room: "304",
    lead: "Hussein",
    ta: "Mohammad"
  },
  // AM (B) classes
  "KG B": {
    room: "157C",
    lead: "Zainab M",
    ta: "Iqra"
  },
  "Grade 1B": {
    room: "157",
    lead: "Parveen",
    ta: "Jana"
  },
  "Grade 2B": {
    room: "157A",
    lead: "Jennah",
    ta: "Deena"
  },
  "Grade 3B": {
    room: "302",
    lead: "Flora",
    ta: "Aisha"
  },
  "Grade 4B": {
    room: "303",
    lead: "Imran",
    ta: "Hakeem"
  },
  "Grade 5B": {
    room: "306B",
    lead: "Mahnoor",
    ta: "Yasmine Lo"
  },
  "Grade 6B": {
    room: "150A",
    lead: "Shakeel",
    ta: "Bassam / Talal (to cover?)"
  },
  // Combined upper classes
  "7/8 Boys": {
    room: "113",
    teachers: "AbdulWahab & Baraa"
  },
  "7/8 Girls": {
    room: "104",
    lead: "Tasneem",
    ta: "Meriem or Hana"
  },
  "Grade 9/10": {
    room: "105",
    lead: "Yazeed (combined)"
  },
  // PM classes
  "Pre-K PM": {
    room: "301",
    lead: "Manal"
  },
  "KG PM": {
    room: "157B",
    lead: "Suhaila"
  },
  "Grade 1 PM": {
    room: "157C",
    lead: "Ladunna",
    ta: "Zubaida"
  },
  "Grade 2 PM": {
    room: "302",
    lead: "Zaynee",
    ta: "Zainab T"
  },
  "Grade 3 PM": {
    room: "306B",
    lead: "Nouraldden Qandeel",
    ta: "Abdullah Ali"
  },
  "Grade 4 PM": {
    room: "306A",
    lead: "Nouraldden Qandeel",
    ta: "Abdullah Ali"
  },
  "Grade 5/6 PM": {
    room: "105",
    lead: "Mazen Alhirsh"
  },
  // Online is taught in three grade bands rather than one class.
  "Online": {
    teachers: "Grades 1&2: Tasnim Elmaghribi · 3&4: Nisma · 5&6: Hiba Jawad"
  }
};

/**
 * Returns today's date in YYYY-MM-DD in local time.
 */
function todayISO(date = new Date()) {
  const tz = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return tz.toISOString().slice(0, 10);
}

/**
 * Simple id
 */
function uid() {
  return Math.random().toString(36).slice(2, 10);
}

/**
 * Generate UUID for Supabase (browser-compatible)
 */
// eslint-disable-next-line no-unused-vars
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

/* =========================================================
   ROSTER PDF EXPORT
   ========================================================= */

const pdfSafeName = (s) =>
  (s || 'roster').replace(/[^\w.-]+/g, '-').replace(/^-+|-+$/g, '') || 'roster';

/**
 * Builds and downloads a roster as a PDF.
 *
 * jsPDF is pulled in with a dynamic import so it stays out of the main bundle:
 * the library is several times the size of this app's own code and is only
 * needed when someone actually clicks Download.
 */
async function downloadRosterPdf({ title, subtitle, students, filename }) {
  const [{ jsPDF }, autoTableMod] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable'),
  ]);
  const autoTable = autoTableMod.default || autoTableMod.autoTable;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  doc.setFontSize(16);
  doc.setTextColor(124, 29, 29);
  doc.text(title, 40, 44);

  if (subtitle) {
    doc.setFontSize(10);
    doc.setTextColor(90);
    doc.text(subtitle, 40, 62);
  }

  // Accepts both shapes: camelCase from app state, snake_case straight from Supabase.
  const body = students.map((s, i) => {
    const p = s.parentInfo || s.parent_info || {};
    return [
      i + 1,
      s.name || '',
      p.name || '',
      p.phone || '',
      p.email || '',
      p.allergies || '',
      p.medicalConditions || '',
    ];
  });

  autoTable(doc, {
    startY: subtitle ? 76 : 58,
    head: [['#', 'Student', 'Parent/Guardian', 'Phone', 'Email', 'Allergies', 'Medical']],
    body,
    styles: { fontSize: 8, cellPadding: 4, overflow: 'linebreak' },
    headStyles: { fillColor: [124, 29, 29], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 248, 248] },
    // Landscape letter is 792pt wide; autotable's default 40pt margins leave
    // 712pt usable. These total 710, so nothing is squeezed and long values
    // wrap rather than overflow.
    columnStyles: {
      0: { cellWidth: 26, halign: 'right' },
      1: { cellWidth: 128 },
      2: { cellWidth: 124 },
      3: { cellWidth: 84 },
      4: { cellWidth: 160 },
      5: { cellWidth: 94 },
      6: { cellWidth: 94 },
    },
    didDrawPage: () => {
      doc.setFontSize(8);
      doc.setTextColor(130);
      doc.text(`Generated ${new Date().toLocaleDateString()}`, 40, pageHeight - 20);
      doc.text(
        `Page ${doc.internal.getNumberOfPages()}`,
        pageWidth - 60,
        pageHeight - 20
      );
    },
  });

  doc.save(filename);
}

/* =========================================================
   SEED STUDENT ROSTER FROM PDF
   ========================================================= */

function createSeededStudents() {
  return {
    // AM CLASSES
    "Pre-K": [
      { id: uid(), name: "Afnan Kavi" },
      { id: uid(), name: "Aliya Genc" },
      { id: uid(), name: "Amal Farooq" },
      { id: uid(), name: "Amin Ahmed" },
      { id: uid(), name: "Ayesha Shafi" },
      { id: uid(), name: "Aya Wanly-Chambers" },
      { id: uid(), name: "Luqmaan Gafoor" },
      { id: uid(), name: "Ryan Bishady" },
      { id: uid(), name: "Yara Nour" },
      { id: uid(), name: "Yaseen Ibrahim" },
      { id: uid(), name: "Zara Nizamuddin" },
      { id: uid(), name: "Aisha Bukhari" },
      { id: uid(), name: "Malik Ismail" },
      { id: uid(), name: "Noah Mazin" },
      { id: uid(), name: "Zakariya Haberkorn" },
      { id: uid(), name: "Nyla Yousaf" },
      { id: uid(), name: "Nyle Syed" },
      { id: uid(), name: "Tanya Rahman" },
      { id: uid(), name: "Ghaith Thiab" },
    ],
    "KG A": [
      { id: uid(), name: "Ahad Khan" },
      { id: uid(), name: "Aydin Ghaznavi" },
      { id: uid(), name: "Isa Hijaz" },
      { id: uid(), name: "Omar Mokidm" },
      { id: uid(), name: "Musa Mehmood" },
      { id: uid(), name: "Farida Ahmed" },
      { id: uid(), name: "Aileen Hussain" },
      { id: uid(), name: "Madina Kamyab" },
    ],
    "KG B": [
      { id: uid(), name: "Abdul Rahman" },
      { id: uid(), name: "Aya Shamim" },
      { id: uid(), name: "Azaan Saleem" },
      { id: uid(), name: "Hadi Shibly" },
      { id: uid(), name: "Omar Kawasmy" },
      { id: uid(), name: "Yamen Ghunaim" },
      { id: uid(), name: "Yusuf Shafi" },
      { id: uid(), name: "Zayn Ariff" },
      { id: uid(), name: "Daneen Sami" },
      { id: uid(), name: "Ayla Durmus" },
      { id: uid(), name: "Marwa Ibrahim" },
      { id: uid(), name: "Haddy Bassiouny" },
      { id: uid(), name: "Haniya Usman" },
      { id: uid(), name: "Sammy Beard" },
      { id: uid(), name: "Ahmad Jamal Hassan" },
      { id: uid(), name: "Ayse Ceker" },
    ],
    "Grade 1A": [
      { id: uid(), name: "Adel Eleryan" },
      { id: uid(), name: "Hasan Sharma" },
      { id: uid(), name: "Lara Nabhan" },
      { id: uid(), name: "Musawwir Muhammad" },
      { id: uid(), name: "Rafae Ajaib" },
      { id: uid(), name: "Arzaan Shaik" },
      { id: uid(), name: "Jasim Hussein" },
      { id: uid(), name: "Zain Nizamuddin" },
      { id: uid(), name: "Shahan Syed" },
      { id: uid(), name: "Inaya Kemal" },
    ],
    "Grade 1B": [
      { id: uid(), name: "Amal Mir" },
      { id: uid(), name: "Ammara Akbar" },
      { id: uid(), name: "Asiya Jamal" },
      { id: uid(), name: "Eesa Ebrahiim" },
      { id: uid(), name: "Hashim Ahmed" },
      { id: uid(), name: "Hatem Kawasmy" },
      { id: uid(), name: "Ibrahim Atif" },
      { id: uid(), name: "Ibrahim Hamid" },
      { id: uid(), name: "Mekaail Atif" },
      { id: uid(), name: "Noor Akbar" },
      { id: uid(), name: "Noorah Moghis" },
      { id: uid(), name: "Syed Zain Aarfi" },
      { id: uid(), name: "Omar Qawasmeh" },
      { id: uid(), name: "Suleman Jawad" },
      { id: uid(), name: "Zayd Siddiqui" },
      { id: uid(), name: "Amal Haberkorn" },
    ],
    "Grade 2A": [
      { id: uid(), name: "Adam Sheik" },
      { id: uid(), name: "Daud Khan" },
      { id: uid(), name: "Ibrahim Genc" },
      { id: uid(), name: "Myra Mazahir" },
      { id: uid(), name: "Maira Hussaini" },
      { id: uid(), name: "Nura Khan" },
      { id: uid(), name: "Daphne Gulsuner" },
      { id: uid(), name: "Zimal Samla" },
      { id: uid(), name: "Yassine Ayed" },
      { id: uid(), name: "Yousef Ismail" },
    ],
    "Grade 2B": [
      { id: uid(), name: "Adam Bishady" },
      { id: uid(), name: "Adam Muzaffar" },
      { id: uid(), name: "Ali Frooq" },
      { id: uid(), name: "Eliza Nunez" },
      { id: uid(), name: "Fahad Syed" },
      { id: uid(), name: "Isa Khan" },
      { id: uid(), name: "Jad Allaga" },
      { id: uid(), name: "Mikael Amin" },
      { id: uid(), name: "Mohammad Armaan" },
      { id: uid(), name: "Musa Shamim" },
      { id: uid(), name: "Raha Jamil" },
      { id: uid(), name: "Selma Abanami" },
      { id: uid(), name: "Yushaa Bhatti" },
      { id: uid(), name: "Yusuf Ibrahim" },
      { id: uid(), name: "Zarun Paracha" },
      { id: uid(), name: "Zia Ariff" },
      { id: uid(), name: "Amna Bukhari" },
      { id: uid(), name: "Hamed Qawasmeh" },
      { id: uid(), name: "Sarah Mahmood" },
      { id: uid(), name: "Zynah Khan" },
    ],
    "Grade 3A": [
      { id: uid(), name: "Abdullah Waqar" },
      { id: uid(), name: "Daniyal Munir" },
      { id: uid(), name: "Leena Wanly-Hunt" },
      { id: uid(), name: "Momina Shuaib" },
      { id: uid(), name: "Noah Ghaznavi" },
      { id: uid(), name: "Rameez Khan" },
      { id: uid(), name: "Sami Shibly" },
      { id: uid(), name: "Sania Sharma" },
      { id: uid(), name: "Sophia Hijaz" },
      { id: uid(), name: "Zaidan Ajaib" },
      { id: uid(), name: "Zane Khabsa" },
      { id: uid(), name: "Zuhair Hakim" },
      { id: uid(), name: "Zainab Syed" },
      { id: uid(), name: "Zayd Daher" },
      { id: uid(), name: "Khalil Jamal" },
      { id: uid(), name: "Imran Ismail" },
      { id: uid(), name: "Liyaan Kazi" },
      { id: uid(), name: "Fareed Abdurahman" },
    ],
    "Grade 3B": [
      { id: uid(), name: "Aleena Alemeshet" },
      { id: uid(), name: "Amin Janjua" },
      { id: uid(), name: "Haneen Mughal" },
      { id: uid(), name: "Hassan Ahmed" },
      { id: uid(), name: "Laya Ghunaim" },
      { id: uid(), name: "Maya Janjua" },
      { id: uid(), name: "Sonia Saidi" },
      { id: uid(), name: "Alishba Sardar" },
      { id: uid(), name: "Aylin Durmus" },
      { id: uid(), name: "Husna Ibrahim" },
      { id: uid(), name: "Zoha Wasiq" },
      { id: uid(), name: "Hassan Yasir" },
      { id: uid(), name: "Eisa Sadiq" },
      { id: uid(), name: "Ayoub Mohamed" },
    ],
    "Grade 4A": [
      { id: uid(), name: "Aisha Khan" },
      { id: uid(), name: "Ayesha Karar" },
      { id: uid(), name: "Ibrahim Umair" },
      { id: uid(), name: "Sophia Eleryan" },
      { id: uid(), name: "Soraya Genc" },
      { id: uid(), name: "Sulayman Gafoor" },
      { id: uid(), name: "Yasameen Genc" },
      { id: uid(), name: "Majeed Abdul" },
      { id: uid(), name: "Zoha Hamid" },
      { id: uid(), name: "Sandy Darwish" },
      { id: uid(), name: "Haaniya Mohammed" },
      { id: uid(), name: "Zayn Nadeem" },
      { id: uid(), name: "Khalid Rashid" },
      { id: uid(), name: "Maryam Brey" },
    ],
    "Grade 4B": [
      { id: uid(), name: "Afiya Sar Ahmed" },
      { id: uid(), name: "Aisha Hodzic" },
      { id: uid(), name: "Husna Abdurahman" },
      { id: uid(), name: "Musa Hussain" },
      { id: uid(), name: "Nadeen Abader" },
      { id: uid(), name: "Natalia Nunez" },
      { id: uid(), name: "Rayyan Saleem" },
      { id: uid(), name: "Zara Chaudhry" },
      { id: uid(), name: "Layla Ahmed" },
      { id: uid(), name: "Zain Elgohary" },
      { id: uid(), name: "Ahmed-Ghufran Iftikhar" },
      { id: uid(), name: "Sarah Ahmad" },
      { id: uid(), name: "Samiya Habibeolla" },
    ],
    "Grade 5A": [
      { id: uid(), name: "Aliza Sardar" },
      { id: uid(), name: "Anya Khan" },
      { id: uid(), name: "Asiya Hussain" },
      { id: uid(), name: "Ayah Sami" },
      { id: uid(), name: "Aydin Hakim" },
      { id: uid(), name: "Fatima Kaukab" },
      { id: uid(), name: "Hamza Naeem" },
      { id: uid(), name: "Rayhan Mir" },
      { id: uid(), name: "Rayyan Mughal" },
      { id: uid(), name: "Sireen Hussain" },
      { id: uid(), name: "Yusuf Muzaffar" },
      { id: uid(), name: "Zayyan Akhtar" },
      { id: uid(), name: "Eyad Elbassuoni" },
      { id: uid(), name: "Ezzeldeen Amr" },
    ],
    "Grade 5B": [
      { id: uid(), name: "Areej Siddiqui" },
      { id: uid(), name: "Hashir Usman" },
      { id: uid(), name: "Rizwan Mohamood" },
      { id: uid(), name: "Mina Mehmood" },
      { id: uid(), name: "Faris Daher" },
      { id: uid(), name: "Myra Asad" },
      { id: uid(), name: "Liana Rahman" },
      { id: uid(), name: "Zidan Iqbal" },
      { id: uid(), name: "Umar Syed" },
      { id: uid(), name: "Zain Mahmood" },
      { id: uid(), name: "Ibrahim Nune" },
      { id: uid(), name: "Munazzah Mohammed" },
      { id: uid(), name: "Jude Khabsa" },
      { id: uid(), name: "Bayan Rahim" },
    ],
    "Grade 6A": [
      { id: uid(), name: "Faisal Abanami" },
      { id: uid(), name: "Zainab Jabeen" },
      { id: uid(), name: "Lamees Wanly-Chambers" },
      { id: uid(), name: "Safa Abdul" },
      { id: uid(), name: "Adam Alemeshet" },
      { id: uid(), name: "Omar Khatab" },
      { id: uid(), name: "Zainab Ahmed" },
      { id: uid(), name: "Mishal Farhad" },
      { id: uid(), name: "Yasmine Ayed" },
      { id: uid(), name: "Zakariya Bhatti" },
      { id: uid(), name: "Zayyan Ramlan" },
      { id: uid(), name: "Yusuf Khalil" },
    ],
    "Grade 6B": [
      { id: uid(), name: "Daaniya Mazahir" },
      { id: uid(), name: "Ainoor Sadiq" },
      { id: uid(), name: "Anam Farooq" },
      { id: uid(), name: "Arshan Kavi" },
      { id: uid(), name: "Maryam Ismail" },
      { id: uid(), name: "Faruk Hodzic" },
      { id: uid(), name: "Hassan Rashid" },
      { id: uid(), name: "Raaya Paracha" },
      { id: uid(), name: "Raif Jamil" },
      { id: uid(), name: "Zaaim Safiullah" },
      { id: uid(), name: "Aziz Uppal" },
      { id: uid(), name: "Zainab Muzaffar" },
      { id: uid(), name: "Adam Khorshid" },
      { id: uid(), name: "Kareem Abader" },
      { id: uid(), name: "Ismaeel Rashid" },
      { id: uid(), name: "Hameed Olaniyan" },
      { id: uid(), name: "Iesa Chawdhary" },
    ],
    "Grade 7": [
      { id: uid(), name: "Aaliyah Lo Presti" },
      { id: uid(), name: "Abyan Akanda" },
      { id: uid(), name: "Ahyan Akanda" },
      { id: uid(), name: "Ameer Akif Ahmed" },
      { id: uid(), name: "Jannat Amin" },
      { id: uid(), name: "Momen Elebaid" },
      { id: uid(), name: "Muhammad Zafar" },
      { id: uid(), name: "Muhammad Aaban Karar" },
      { id: uid(), name: "Mustafa Saidi" },
      { id: uid(), name: "Saad Rasool" },
      { id: uid(), name: "Laith Beard" },
      { id: uid(), name: "Mikail Shaikh" },
      { id: uid(), name: "Zeba Yunus" },
      { id: uid(), name: "Ehsan Osmanbhoy" },
      { id: uid(), name: "Maryam Zeirideen" },
      { id: uid(), name: "Yousuf Mahmood" },
      { id: uid(), name: "Alaa Alogaily" },
      { id: uid(), name: "Hiyam Nassir" },
      { id: uid(), name: "Mariam Amr" },
      { id: uid(), name: "Yassin Ahmed" },
      { id: uid(), name: "Ameer Khatri" },
    ],
    "Grade 8": [
      { id: uid(), name: "Emaan Siddiqui" },
      { id: uid(), name: "Fatima Abrar" },
      { id: uid(), name: "Hamza Abdelqader" },
      { id: uid(), name: "Ibaad Uddin" },
      { id: uid(), name: "Muriyum Khawar" },
      { id: uid(), name: "Nuhaa Khurram" },
      { id: uid(), name: "Rayhan Mohamood" },
      { id: uid(), name: "Yasser Deraz" },
      { id: uid(), name: "Aaira Kidwai" },
      { id: uid(), name: "Ayzah Wasiq" },
      { id: uid(), name: "Omar Farhan" },
      { id: uid(), name: "Omar Ismail" },
      { id: uid(), name: "Tairah Olaniyan" },
      { id: uid(), name: "Mishkah Sajeel" },
    ],
    "Grade 9/10": [
      { id: uid(), name: "Aleena Zafar" },
      { id: uid(), name: "Danish Syed" },
      { id: uid(), name: "Sarah Khorshid" },
      { id: uid(), name: "Yusuf Iqbal" },
      { id: uid(), name: "Sara Ifran" },
      { id: uid(), name: "Reem Darwish" },
      { id: uid(), name: "Rohail Khan" },
      { id: uid(), name: "Eshaal Naeem" },
    ],

    // PM CLASSES
    "Pre-K PM": [
      { id: uid(), name: "Amnah Aamir" },
      { id: uid(), name: "Mahnoor Omar" },
      { id: uid(), name: "Noor Kamel" },
      { id: uid(), name: "Zayna Siddiqui" },
      { id: uid(), name: "Princilla Anangg" },
      { id: uid(), name: "Ibrahim Khan" },
      { id: uid(), name: "Khalifa Awan" },
      { id: uid(), name: "Elleen Mahyoub" },
      { id: uid(), name: "Luca Acosta" },
      { id: uid(), name: "Ayana El-Saadoun" },
    ],
    "KG PM": [
      { id: uid(), name: "Arta Laksmono" },
      { id: uid(), name: "Hannah Hosany" },
      { id: uid(), name: "Haris Qadri" },
      { id: uid(), name: "Adam Naini" },
      { id: uid(), name: "Hanif Baig" },
      { id: uid(), name: "Aws Alshehri" },
      { id: uid(), name: "Kemal Mohammedamin" },
      { id: uid(), name: "Aiden Arbab" },
      { id: uid(), name: "Aleeza Adeel" },
      { id: uid(), name: "Norah Kazi" },
      { id: uid(), name: "Sophia Syed" },
      { id: uid(), name: "Maira Mehroz" },
    ],
    "Grade 1 PM": [
      { id: uid(), name: "Lelah Soliman" },
      { id: uid(), name: "Nuhad Yeamin" },
      { id: uid(), name: "Safiya Adem" },
      { id: uid(), name: "Anya Rahman" },
      { id: uid(), name: "Mila Malanovic" },
      { id: uid(), name: "Ayyan Jihad" },
      { id: uid(), name: "Anne Mohammad" },
    ],
    "Grade 2 PM": [
      { id: uid(), name: "Eisa Sadiq" },
      { id: uid(), name: "Fabiha Naya" },
      { id: uid(), name: "Humza Qadri" },
      { id: uid(), name: "Maya Hessen" },
      { id: uid(), name: "Yusuf Kamel" },
      { id: uid(), name: "Zaviyar Siddiqui" },
      { id: uid(), name: "Liyana Arbab" },
      { id: uid(), name: "Mariam Amdani" },
      { id: uid(), name: "Princeton Anangga" },
      { id: uid(), name: "Alexander Jaamour" },
      { id: uid(), name: "Zarish Javed" },
      { id: uid(), name: "Zoya Farooq" },
    ],
    "Grade 3 PM": [
      { id: uid(), name: "Iman Mohammedamin" },
      { id: uid(), name: "Zidan Soliman" },
      { id: uid(), name: "Adeen Rahman" },
      { id: uid(), name: "Affan Yasir" },
      { id: uid(), name: "Ladun Fuad" },
      { id: uid(), name: "Azlan Qadri" },
    ],
    "Grade 4 PM": [
      { id: uid(), name: "Bayan Mohammedamin" },
      { id: uid(), name: "Miqail Omar" },
      { id: uid(), name: "Malik Acosta" },
      { id: uid(), name: "Eva Constantinescu" },
    ],
    "Grade 5 PM": [
      { id: uid(), name: "Hania Hessen" },
      { id: uid(), name: "Inaaya Yeamin" },
      { id: uid(), name: "Safeerah Mahmud" },
      { id: uid(), name: "Nuwaira Mahmud" },
      { id: uid(), name: "Eyad Alogaily" },
    ],
    "Grade 6 PM": [
      { id: uid(), name: "Yaseen Soliman" },
      { id: uid(), name: "Mustafa Saeed" },
      { id: uid(), name: "Kareem Radi" },
      { id: uid(), name: "Danial Rashid" },
      { id: uid(), name: "Amaar Yasir" },
    ],
  };
}

/* =========================================================
   SEED MAPS 2025–2026 CALENDAR
   ========================================================= */

function createSeededEvents() {
  const events = {};

  const add = (date, title) => {
    if (!events[date]) events[date] = [];
    events[date].push({ id: uid(), title });
  };

  // 2026-27, from the FINAL SS 26-27 academic calendar.
  // SEMESTER 1
  add("2026-09-06", "Open House / Meet the Teacher");
  add("2026-09-13", "First Day of Sunday School");
  add("2026-10-04", "Non-Student Day – PD");
  add("2026-11-15", "Non-Student Day – Teachers Day PD");
  add("2026-11-29", "No School – Thanksgiving Break");
  add("2026-12-20", "No School – Winter Break");
  add("2026-12-27", "No School – Winter Break");

  // SEMESTER 2
  add("2027-01-03", "First Day of Semester 2");
  add("2027-01-17", "No School – Martin Luther King Jr. Day");
  add("2027-02-08", "First Day of Ramadan");
  add("2027-02-14", "Ramadan Schedule");
  add("2027-02-21", "Ramadan Schedule");
  add("2027-02-28", "No School – Ramadan Break");
  add("2027-03-07", "No School – Eid Al-Fitr");
  add("2027-04-18", "No School – Spring Break");
  add("2027-05-16", "No School – Day of Arafah & Eid");
  add("2027-05-17", "Eid Al-Adha");
  add("2027-05-30", "No School – Memorial Day Weekend");
  add("2027-06-06", "Last Day of Instruction / Pre-K Promotion");
  add("2027-06-13", "End of Year Carnival");

  // Regular Sunday Classes, first day through last day of instruction.
  // Dates already carrying an event above are left alone, so no-school
  // Sundays are not overwritten with a class.
  const start = new Date("2026-09-13T00:00:00");
  const end = new Date("2027-06-06T00:00:00");
  for (
    let d = new Date(start);
    d.getTime() <= end.getTime();
    d.setDate(d.getDate() + 7)
  ) {
    const iso = todayISO(d);
    if (!events[iso]) {
      add(iso, "Regular Sunday Class");
    }
  }

  return events;
}

/* =========================================================
   PREDEFINED ADMIN USERS
   ========================================================= */

function getAdminUsers() {
  return [
    {
      email: "mallakattwa@gmail.com",
      username: "mallak",
      password: "maps2025",
      role: "admin",
      name: "Mallak Attwa",
      title: "Vice Principal",
    },
    {
      email: "haya.awadallah1@gmail.com",
      username: "haya",
      password: "simba1",
      role: "admin",
      name: "Haya Awadallah",
      title: "Secretary",
    },
    {
      email: "shanafy@outlook.com",
      username: "sarah",
      password: "maps2025",
      role: "admin",
      name: "Sarah Hanafy",
      title: "Social Media/Volunteer Coordinator",
    },
    {
      email: "BarryShehadeh@gmail.com",
      username: "baraa",
      password: "maps2025",
      role: "admin",
      name: "Baraa Shehadeh",
      title: "Head Teacher",
    },
    {
      email: "shaymaz2013@gmail.com",
      username: "shayma",
      password: "maps2025",
      role: "admin",
      name: "Shayma Alshakshir",
      title: "Acting Principal",
    },
  ];
}

/* =========================================================
   STATE LOAD / SAVE
   ========================================================= */

function getDefaultState() {
  return {
    users: getAdminUsers(),
    currentUser: null,
    studentsByGrade: createSeededStudents(),
    attendance: {},
    attendanceLocked: false,
    events: createSeededEvents(),
    pendingUsers: [],
    announcements: [],
    lunchOrders: [],
    accessCodes: [
      { id: uid(), name: "Copier", code: "5312" },
      { id: uid(), name: "Storage Code", code: "5339" },
      { id: uid(), name: "Stairs Code", code: "4918" },
      { id: uid(), name: "WIFI", code: "PW: LoveAllah" }
    ],
  };
}

async function loadStateFromSupabase() {
  try {
    console.log('🔄 Loading state from Supabase...');

    // Load all data in parallel
    const [usersRes, studentsRes, attendanceRes, eventsRes, announcementsRes, codesRes] = await Promise.all([
      supabase.from('users').select('*'),
      // Only the current intake feeds rosters, attendance and class lists.
      // Archived years are read separately by the Archived Students tab.
      supabase.from('students').select('*').eq('status', 'active'),
      supabase.from('attendance').select('*').eq('school_year', CURRENT_SCHOOL_YEAR),
      supabase.from('events').select('*'),
      supabase.from('announcements').select('*'),
      supabase.from('access_codes').select('*')
    ]);

    // Check for errors
    if (usersRes.error) console.error('Error loading users:', usersRes.error);
    if (studentsRes.error) console.error('Error loading students:', studentsRes.error);
    if (attendanceRes.error) console.error('Error loading attendance:', attendanceRes.error);
    if (eventsRes.error) console.error('Error loading events:', eventsRes.error);
    if (announcementsRes.error) console.error('Error loading announcements:', announcementsRes.error);
    if (codesRes.error) console.error('Error loading access codes:', codesRes.error);

    // Transform students array back into studentsByGrade object
    const studentsByGrade = {};
    const seenStudents = {}; // Track seen students to prevent duplicates

    (studentsRes.data || []).forEach(student => {
      if (!studentsByGrade[student.grade]) {
        studentsByGrade[student.grade] = [];
        seenStudents[student.grade] = new Set();
      }

      // Only add student if not already seen in this grade
      if (!seenStudents[student.grade].has(student.name)) {
        seenStudents[student.grade].add(student.name);
        studentsByGrade[student.grade].push({
          id: uid(),
          name: student.name,
          parentInfo: student.parent_info || null
        });
      }
    });

    // Transform users from snake_case to camelCase and filter only approved users for login
    const users = (usersRes.data || [])
      .filter(user => user.status === 'approved')  // Only load approved users into state
      .map(user => ({
        email: user.email,
        password: user.password,
        name: user.name,
        role: user.role,
        gradeLevel: user.grade_level || '',
        title: user.title || '',
        isTeamLead: user.is_team_lead || false,
        teamLeadGrade: user.team_lead_grade || '',
        status: user.status || 'approved'
      }));

    // Transform attendance from Supabase array to component format
    // Component format: attendance[date][grade][studentId] = { status }
    const attendance = {};

    // Transform events array from Supabase into object keyed by date
    const events = {};
    (eventsRes.data || []).forEach(event => {
      if (!events[event.date]) {
        events[event.date] = [];
      }
      events[event.date].push({
        id: event.id || uid(),
        title: event.title
      });
    });

    console.log(`📅 Loaded ${eventsRes.data?.length || 0} events from Supabase`);

    // Transform access codes
    const accessCodes = (codesRes.data || []).map(code => ({
      id: uid(),
      name: code.email || '',  // Using email field as name for now
      code: code.code
    }));

    const state = {
      users: users,
      currentUser: null,
      studentsByGrade: studentsByGrade,
      attendance: attendance,
      events: Object.keys(events).length > 0 ? events : createSeededEvents(),
      pendingUsers: [],  // No longer used - pending users filtered in Approvals component
      announcements: announcementsRes.data || [],
      lunchOrders: [],
      accessCodes: accessCodes.length > 0 ? accessCodes : [
        { id: uid(), name: "Copier", code: "5312" },
        { id: uid(), name: "Storage Code", code: "5339" },
        { id: uid(), name: "Stairs Code", code: "4918" },
        { id: uid(), name: "WIFI", code: "PW: LoveAllah" }
      ]
    };

    console.log('✅ State loaded from Supabase:', {
      users: users.length,
      students: (studentsRes.data || []).length,
      grades: Object.keys(studentsByGrade).length
    });

    return state;
  } catch (error) {
    console.error('❌ Error loading from Supabase:', error);
    return getDefaultState();
  }
}

async function saveStateToSupabase(state, currentUser = null) {
  try {
    console.log('💾 Saving state to Supabase...');

    // Transform studentsByGrade object into flat array
    const studentsArray = [];
    for (const [grade, students] of Object.entries(state.studentsByGrade || {})) {
      students.forEach(student => {
        if (student && student.name) {
          studentsArray.push({
            name: student.name,
            grade: grade,
            parent_info: student.parentInfo || null,
            school_year: CURRENT_SCHOOL_YEAR,
            status: 'active'
          });
        }
      });
    }

    // Transform attendance object to array
    // New format: attendance[date][grade][studentId] = { status }
    const attendanceArray = [];
    for (const [date, gradeData] of Object.entries(state.attendance || {})) {
      for (const [grade, studentData] of Object.entries(gradeData || {})) {
        for (const [studentId, record] of Object.entries(studentData || {})) {
          // Find student name by ID
          const gradeStudents = state.studentsByGrade[grade] || [];
          const student = gradeStudents.find(s => s.id === studentId);

          if (student && record.status) {
            // Convert status to boolean (P = true, A/L = false)
            const present = record.status === "P";

            attendanceArray.push({
              date: date,
              grade: grade,
              student_name: student.name,
              present: present,
              recorded_by: currentUser ? currentUser.name : null,
              school_year: CURRENT_SCHOOL_YEAR
            });
          }
        }
      }
    }

    // Users are managed separately through login/signup/approval flows
    // Do NOT sync users table here to avoid conflicts with pending_users workflow

    // Clear and insert students.
    // Both filters matter: school_year keeps archived years untouched, and
    // status keeps a student archived mid-year from being resurrected.
    if (studentsArray.length > 0) {
      await supabase
        .from('students')
        .delete()
        .eq('school_year', CURRENT_SCHOOL_YEAR)
        .eq('status', 'active');
      await supabase.from('students').insert(studentsArray);
    }

    // Clear and insert attendance for the current year only.
    // Note: this still rewrites every current-year attendance row from one
    // browser's state, so two teachers submitting at once can overwrite each
    // other. Scoping the delete to the submitted (date, grade) is the fix.
    if (attendanceArray.length > 0) {
      await supabase
        .from('attendance')
        .delete()
        .eq('school_year', CURRENT_SCHOOL_YEAR);
      await supabase.from('attendance').insert(attendanceArray);
    }

    // Clear and insert events
    // Transform events object into array for Supabase
    const eventsArray = [];
    for (const [date, eventList] of Object.entries(state.events || {})) {
      eventList.forEach(event => {
        eventsArray.push({
          id: event.id,
          date: date,
          title: event.title
        });
      });
    }
    if (eventsArray.length > 0) {
      await supabase.from('events').delete().neq('id', '__NONE__');
      await supabase.from('events').insert(eventsArray);
    }

    // Clear and insert announcements
    if (state.announcements && state.announcements.length > 0) {
      await supabase.from('announcements').delete().neq('id', '__NONE__');
      await supabase.from('announcements').insert(state.announcements);
    }

    // Clear and insert access codes
    if (state.accessCodes && state.accessCodes.length > 0) {
      await supabase.from('access_codes').delete().neq('code', '__NONE__');
      const codesForSupabase = state.accessCodes.map(code => ({
        code: code.code,
        email: code.name || '',  // Map name to email field
        expires_at: Date.now() + (365 * 24 * 60 * 60 * 1000) // 1 year from now
      }));
      await supabase.from('access_codes').insert(codesForSupabase);
    }

    // Lunch orders feature not implemented yet

    console.log('✅ State saved to Supabase');
  } catch (error) {
    console.error('❌ Error saving to Supabase:', error);
  }
}

/* =========================================================
   PASSWORDS

   Stored as PBKDF2-HMAC-SHA256 with a random per-account salt, written
   as pbkdf2$sha256$<iterations>$<salt>$<hash>. Accounts predating this
   hold a bare plaintext string; those still verify and are rewritten as
   a hash the moment their owner next signs in, so the table drains of
   plaintext without locking anybody out.

   This stops a leaked table from handing over passwords people reuse on
   other sites, which is the damage that spreads furthest. It is not a
   replacement for real authentication: the anon key ships inside the
   published bundle, so whoever holds it can still read and write these
   rows. Supabase Auth is the answer to that, and a much larger job.
   ========================================================= */

const PBKDF2_ITERATIONS = 210000; // OWASP guidance for PBKDF2-HMAC-SHA256
const PBKDF2_KEY_BITS = 256;
const PBKDF2_SALT_BYTES = 16;
const PASSWORD_PREFIX = 'pbkdf2$sha256$';

function bytesToBase64(bytes) {
  let binary = '';
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return window.btoa(binary);
}

function base64ToBytes(value) {
  const binary = window.atob(value);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

// crypto.subtle exists only in a secure context, so this throws on plain
// http. Better to fail loudly than to quietly write a password in the clear.
function requireCrypto() {
  const c = typeof window !== 'undefined' ? window.crypto : null;
  if (!c || !c.subtle) {
    throw new Error('Secure connection required to set a password.');
  }
  return c;
}

async function derivePasswordBits(password, salt, iterations) {
  const c = requireCrypto();
  const key = await c.subtle.importKey(
    'raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveBits']
  );
  const bits = await c.subtle.deriveBits(
    { name: 'PBKDF2', salt, iterations, hash: 'SHA-256' }, key, PBKDF2_KEY_BITS
  );
  return new Uint8Array(bits);
}

function isHashedPassword(stored) {
  return typeof stored === 'string' && stored.startsWith(PASSWORD_PREFIX);
}

async function hashPassword(password) {
  const c = requireCrypto();
  const salt = c.getRandomValues(new Uint8Array(PBKDF2_SALT_BYTES));
  const hash = await derivePasswordBits(password, salt, PBKDF2_ITERATIONS);
  return `${PASSWORD_PREFIX}${PBKDF2_ITERATIONS}$${bytesToBase64(salt)}$${bytesToBase64(hash)}`;
}

// Compares every character rather than stopping at the first difference, so
// how long the check takes says nothing about how much of it was right.
function timingSafeEqual(a, b) {
  if (typeof a !== 'string' || typeof b !== 'string' || a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

async function verifyPassword(password, stored) {
  if (typeof stored !== 'string' || stored === '') return false;
  if (!isHashedPassword(stored)) return timingSafeEqual(password, stored);

  const parts = stored.split('$');
  if (parts.length !== 5) return false;
  const iterations = Number(parts[2]);
  if (!Number.isFinite(iterations) || iterations <= 0) return false;

  try {
    const actual = bytesToBase64(
      await derivePasswordBits(password, base64ToBytes(parts[3]), iterations)
    );
    return timingSafeEqual(actual, parts[4]);
  } catch (err) {
    console.error('Could not verify password:', err);
    return false;
  }
}

/* =========================================================
   REMEMBERED SIGN-IN

   Keeps a signed-in teacher signed in across reloads and browser
   restarts. Only the email address is written to disk, never the
   password, and the account is re-read from Supabase every time the
   site opens: an account that has since been denied, deleted or given
   a different role cannot outlive that change by riding a stale copy
   in storage.
   ========================================================= */

const SESSION_KEY = "mapsSundaySchoolSession";
// Long enough to span a missed Sunday or two. Logging out clears it at once.
const SESSION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

function rememberSession(email) {
  try {
    window.localStorage.setItem(SESSION_KEY, JSON.stringify({ email, savedAt: Date.now() }));
  } catch (err) {
    // Private windows and blocked site data both throw here. Staying signed
    // in is a convenience, so failing to store it must never fail the login.
    console.warn('Could not remember this session:', err);
  }
}

function readRememberedSession() {
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (!saved || !saved.email || typeof saved.savedAt !== 'number') return null;
    if (Date.now() - saved.savedAt > SESSION_MAX_AGE_MS) {
      forgetSession();
      return null;
    }
    return saved.email;
  } catch (err) {
    console.warn('Could not read the saved session:', err);
    return null;
  }
}

function forgetSession() {
  try {
    window.localStorage.removeItem(SESSION_KEY);
  } catch (err) {
    console.warn('Could not clear the saved session:', err);
  }
}

// eslint-disable-next-line no-unused-vars
function saveState(state) {
  // Save only to Supabase (localStorage removed for data integrity)
  // Only Supabase is the source of truth for application data
  saveStateToSupabase(state).catch(err => console.error('Background Supabase save failed:', err));
}

/* =========================================================
   APP ROOT
   ========================================================= */

export default function App() {
  const [state, setState] = useState(getDefaultState());
  const [activeSection, setActiveSection] = useState("calendar");
  const [lastSeenAnnouncement, setLastSeenAnnouncement] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationData, setNotificationData] = useState(null);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [firebaseLoaded, setFirebaseLoaded] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  // Read straight from storage during the first render: restoring a session
  // is asynchronous, and without this the login form paints for a moment
  // before the restore lands. Someone with no saved session skips the wait.
  const [restoringSession, setRestoringSession] = useState(() => !!readRememberedSession());

  // Load state from Supabase on mount
  useEffect(() => {
    async function initializeState() {
      console.log('🚀 Initializing app...');
      try {
      const supabaseState = await loadStateFromSupabase();
      if (supabaseState) {
        // loadStateFromSupabase returns approved users only, so an account
        // that lost its approval simply will not be found here.
        const rememberedEmail = readRememberedSession();
        const account = rememberedEmail
          ? (supabaseState.users || []).find(
              (u) => (u.email || '').toLowerCase() === rememberedEmail.toLowerCase()
            )
          : null;

        if (rememberedEmail && !account) {
          console.log('⚠️ Saved session no longer valid, signing out');
          forgetSession();
        }

        setState(
          account
            ? {
                ...supabaseState,
                currentUser: {
                  email: account.email,
                  role: account.role,
                  name: account.name,
                  title: account.title,
                  gradeLevel: account.gradeLevel,
                  isTeamLead: account.isTeamLead || false,
                  teamLeadGrade: account.teamLeadGrade || null,
                  status: account.status || 'approved',
                },
              }
            : supabaseState
        );
        console.log(
          account ? `✅ Signed back in as ${account.email}` : '✅ Loaded state from Supabase'
        );
      } else {
        console.log('⚠️ Failed to load from Supabase, using default state');
      }
      } catch (err) {
        console.error('❌ Error initializing app:', err);
      } finally {
        // Must always run: leaving this set would hold the app on the
        // spinner forever, which is worse than showing the login form.
        setFirebaseLoaded(true);
        setRestoringSession(false);
      }
    }
    initializeState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load user preferences when user logs in
  useEffect(() => {
    async function loadUserPrefs() {
      if (state.currentUser && state.currentUser.email) {
        const prefs = await getUserPreferences(state.currentUser.email);
        setDarkMode(prefs.darkMode);
        setLastSeenAnnouncement(prefs.lastSeenAnnouncement);
      }
    }
    loadUserPrefs();
  }, [state.currentUser]);

  // DISABLED: Auto-save was wiping out Supabase data
  // All data changes should happen through direct Supabase operations
  // (signup, approval, attendance marking, etc.)
  // useEffect(() => {
  //   if (!firebaseLoaded) return;
  //   saveState(state);
  // }, [state, firebaseLoaded]);

  // Dark mode handler
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Save dark mode preference to Supabase when it changes
    if (state.currentUser && state.currentUser.email) {
      saveUserPreferences(state.currentUser.email, {
        darkMode: darkMode,
        lastSeenAnnouncement: lastSeenAnnouncement
      });
    }
  }, [darkMode, state.currentUser, lastSeenAnnouncement]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  // Check for new announcements and show notification
  useEffect(() => {
    if (!state.currentUser || state.currentUser.role === "admin") return;

    const announcements = state.announcements || [];
    if (announcements.length === 0) return;

    const latestAnnouncement = announcements[0];

    // Check if this is a new announcement the user hasn't seen
    if (lastSeenAnnouncement !== latestAnnouncement.id) {
      setNotificationData(latestAnnouncement);
      setShowNotification(true);
      setLastSeenAnnouncement(latestAnnouncement.id);
    }
  }, [state.announcements, state.currentUser, lastSeenAnnouncement]);

  const dismissNotification = () => {
    setShowNotification(false);
  };

  const user = state.currentUser;

  const handleLogout = () => {
    forgetSession();
    setState((s) => ({ ...s, currentUser: null }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-white dark:from-gray-900 dark:to-gray-900 text-gray-800 dark:text-gray-100 transition-colors duration-200">
      <Header
        user={user}
        onLogout={handleLogout}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        onOpenAccountSettings={() => setShowAccountSettings(true)}
      />

      <main className="container mx-auto max-w-7xl px-16 py-16">
        {restoringSession && !user ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="h-8 w-8 rounded-full border-2 border-[#7c1d1d] dark:border-red-500 border-t-transparent animate-spin" />
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Signing you in...</p>
          </div>
        ) : !user ? (
          <Auth state={state} setState={setState} />
        ) : user.status === "pending" ? (
          <PendingApproval />
        ) : activeSection === "calendar" ? (
          <Calendar state={state} setState={setState} />
        ) : activeSection === "attendance" ? (
          <Attendance state={state} setState={setState} user={user} />
        ) : activeSection === "students" ? (
          <Students state={state} setState={setState} user={user} />
        ) : activeSection === "resources" ? (
          <Resources state={state} setState={setState} user={user} />
        ) : activeSection === "schedule" ? (
          <Schedule />
        ) : activeSection === "lunch-orders" ? (
          <LunchOrders state={state} setState={setState} user={user} />
        ) : activeSection === "access-codes" ? (
          <AccessCodes state={state} setState={setState} user={user} />
        ) : activeSection === "announcements" ? (
          <Announcements state={state} setState={setState} user={user} />
        ) : activeSection === "print" ? (
          <PrintPage />
        ) : activeSection === "approvals" ? (
          canApproveUsers(user) ? (
            <Approvals state={state} setState={setState} />
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">Access Denied</h2>
              <p className="text-gray-600 dark:text-gray-400">You do not have permission to access approvals.</p>
            </div>
          )
        ) : null}
      </main>

      <Footer />

      {/* Announcement Notification Popup */}
      {showNotification && notificationData && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6 border-t-4 border-[#7c1d1d] dark:border-red-600 animate-slideIn">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-2xl">📢</span>
                <h3 className="text-xl font-bold text-[#7c1d1d] dark:text-red-400">New Announcement</h3>
              </div>
              <button
                onClick={dismissNotification}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <h4 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">{notificationData.title}</h4>
            <p className="text-gray-700 dark:text-gray-300 mb-4 whitespace-pre-wrap">{notificationData.message}</p>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Posted by {notificationData.author} on{" "}
              {new Date(notificationData.date).toLocaleString()}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  dismissNotification();
                  setActiveSection("announcements");
                }}
                className="flex-1 bg-[#7c1d1d] dark:bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-[#651818] dark:hover:bg-red-700 font-medium transition-colors"
              >
                View All Announcements
              </button>
              <button
                onClick={dismissNotification}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white font-medium transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Account Settings Modal */}
      {showAccountSettings && user && (
        <AccountSettingsModal
          user={user}
          state={state}
          setState={setState}
          onClose={() => setShowAccountSettings(false)}
          darkMode={darkMode}
          toggleDarkMode={toggleDarkMode}
          onLogout={handleLogout}
        />
      )}
    </div>
  );
}

/* =========================================================
   ACCOUNT SETTINGS MODAL
   ========================================================= */

function AccountSettingsModal({ user, state, setState, onClose, darkMode, toggleDarkMode, onLogout }) {
  const [name, setName] = useState(user.name || "");
  const [email, setEmail] = useState(user.email || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [title, setTitle] = useState(user.title || "");
  const [activeTab, setActiveTab] = useState("account");

  // Archived records carry parent contacts and allergies for children who may
  // no longer attend, so the tab is admin-only.
  const isAdmin = user.role === "admin";

  const [saving, setSaving] = useState(false);

  // Every edit here has to reach Supabase. saveStateToSupabase deliberately
  // skips the users table to stay clear of the pending_users approval flow,
  // so writing to React state alone left the change to evaporate on reload.
  const handleSave = async () => {
    const nextName = name.trim();
    const nextEmail = email.trim();
    const nextTitle = title.trim();
    const changingPassword = !!(newPassword || confirmPassword || currentPassword);

    if (!nextName) {
      alert("Name cannot be empty.");
      return;
    }
    if (!nextEmail) {
      alert("Email cannot be empty.");
      return;
    }
    if (changingPassword) {
      if (newPassword !== confirmPassword) {
        alert("New passwords do not match!");
        return;
      }
      if (!newPassword.trim()) {
        alert("New password cannot be empty!");
        return;
      }
    }

    setSaving(true);
    try {
      // The signed-in object carries no password field, so the old check
      // compared against undefined and could never pass. Read the stored row
      // instead, which also catches a password changed from another device.
      const { data: rows, error: lookupError } = await supabase
        .from('users')
        .select('*')
        .ilike('email', user.email)
        .limit(1);

      if (lookupError || !rows || rows.length === 0) {
        console.error('Could not load the account to update:', lookupError);
        alert("Could not reach your account. Please try again.");
        return;
      }
      const stored = rows[0];

      if (changingPassword && !(await verifyPassword(currentPassword, stored.password))) {
        alert("Current password is incorrect. Cannot change password.");
        return;
      }

      // Email is the handle every lookup and the saved session use, so a
      // collision would quietly make two accounts indistinguishable.
      if (nextEmail.toLowerCase() !== user.email.toLowerCase()) {
        const { data: clash } = await supabase
          .from('users')
          .select('email')
          .ilike('email', nextEmail)
          .limit(1);
        if (clash && clash.length > 0) {
          alert("That email is already used by another account.");
          return;
        }
      }

      const updates = { name: nextName, email: nextEmail, title: nextTitle };
      if (changingPassword) {
        try {
          updates.password = await hashPassword(newPassword);
        } catch (err) {
          console.error('Could not hash the new password:', err);
          alert('Could not set your new password securely. Please try again.');
          return;
        }
      }

      const { error: saveError } = await supabase
        .from('users')
        .update(updates)
        .eq('email', stored.email);

      if (saveError) {
        console.error('Failed to save account settings:', saveError);
        alert(`Could not save your changes: ${saveError.message}`);
        return;
      }

      // Keep the remembered sign-in pointing at the row that just moved,
      // otherwise the next visit looks for an address that no longer exists.
      if (nextEmail.toLowerCase() !== user.email.toLowerCase()) {
        rememberSession(nextEmail);
      }

      setState((s) => ({
        ...s,
        users: s.users.map((u) =>
          u.email === user.email
            ? { ...u, name: nextName, email: nextEmail, title: nextTitle,
                ...(changingPassword ? { password: updates.password } : {}) }
            : u
        ),
        currentUser: { ...s.currentUser, name: nextName, email: nextEmail, title: nextTitle },
      }));

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      alert(
        changingPassword
          ? "Account settings and password updated successfully!"
          : "Account settings updated successfully!"
      );
      onClose();
    } catch (err) {
      console.error('Unexpected error saving account settings:', err);
      alert("Could not save your changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full p-6 border-t-4 border-[#7c1d1d] dark:border-red-600 max-h-[90vh] overflow-y-auto transition-[max-width] ${
        activeTab === "archived" || activeTab === "helpdesk"
          ? "max-w-3xl"
          : activeTab === "help"
          ? "max-w-lg"
          : "max-w-md"
      }`}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-xl font-bold text-[#7c1d1d] dark:text-red-400">Settings</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="flex gap-1 mb-5 border-b border-gray-200 dark:border-gray-600 flex-wrap">
          <SettingsTab
            label="Account"
            active={activeTab === "account"}
            onClick={() => setActiveTab("account")}
          />
          <SettingsTab
            label="Get Help"
            active={activeTab === "help"}
            onClick={() => setActiveTab("help")}
          />
          {isAdmin && (
            <SettingsTab
              label="Help Desk"
              active={activeTab === "helpdesk"}
              onClick={() => setActiveTab("helpdesk")}
            />
          )}
          {isAdmin && (
            <SettingsTab
              label="2025/2026 School Year Roster"
              active={activeTab === "archived"}
              onClick={() => setActiveTab("archived")}
            />
          )}
        </div>

        {activeTab === "account" && (
        <>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Full Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title/Role</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Grade Level</label>
            <input
              type="text"
              value={user.gradeLevel || "N/A"}
              disabled
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Grade level cannot be changed</p>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-600 pt-4 mt-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Dark Mode</label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Toggle dark mode on or off</p>
              </div>
              <button
                type="button"
                onClick={toggleDarkMode}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  darkMode ? 'bg-[#7c1d1d]' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    darkMode ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-600 pt-4 mt-4">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Change Password (Optional)</h4>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Current Password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password to change"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] dark:focus:ring-red-600 focus:border-transparent dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] dark:focus:ring-red-600 focus:border-transparent dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] dark:focus:ring-red-600 focus:border-transparent dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-[#7c1d1d] dark:bg-red-900 text-white px-4 py-2 rounded-lg hover:bg-[#651818] dark:hover:bg-red-800 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-200 font-medium transition-colors"
          >
            Cancel
          </button>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
          <button
            onClick={() => {
              if (window.confirm("Are you sure you want to log out?")) {
                onLogout();
              }
            }}
            className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 font-medium transition-colors"
          >
            Log Out
          </button>
        </div>
        </>
        )}

        {activeTab === "help" && <HelpTicketForm user={user} />}

        {activeTab === "helpdesk" && isAdmin && <HelpDesk />}

        {activeTab === "archived" && isAdmin && <ArchivedStudents />}
      </div>
    </div>
  );
}

function SettingsTab({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
        active
          ? "border-[#7c1d1d] dark:border-red-500 text-[#7c1d1d] dark:text-red-400"
          : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
      }`}
    >
      {label}
    </button>
  );
}


/* =========================================================
   HELP TICKETS
   Teachers file an issue from Settings > Get Help; admins work
   them in Settings > Help Desk.
   ========================================================= */

// The table is created by scripts/create-support-tickets-table.sql. Until that
// runs, PostgREST answers with PGRST205 rather than an empty list, so say so
// plainly instead of showing a generic failure.
const TICKETS_TABLE_MISSING =
  'The help desk table does not exist yet. Run scripts/create-support-tickets-table.sql in the Supabase SQL editor.';

function ticketErrorMessage(error) {
  if (error && (error.code === 'PGRST205' || /schema cache/i.test(error.message || ''))) {
    return TICKETS_TABLE_MISSING;
  }
  return error?.message || 'Something went wrong.';
}

function formatTicketTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

function HelpTicketForm({ user }) {
  const [name, setName] = useState(user.name || '');
  const [issue, setIssue] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { ok: boolean, message: string }

  const submit = async () => {
    const n = name.trim();
    const text = issue.trim();
    if (!n) { setResult({ ok: false, message: 'Please enter your name.' }); return; }
    if (!text) { setResult({ ok: false, message: 'Please describe the issue.' }); return; }

    setBusy(true);
    setResult(null);
    const { error } = await supabase.from('support_tickets').insert({
      reporter_name: n,
      reporter_email: user.email || null,
      reporter_title: user.title || null,
      reporter_class: user.gradeLevel || null,
      issue: text,
      status: 'open',
    });
    setBusy(false);

    if (error) {
      console.error('Failed to file help ticket:', error);
      setResult({ ok: false, message: ticketErrorMessage(error) });
      return;
    }
    setIssue('');
    setResult({ ok: true, message: 'Sent. An administrator will take a look.' });
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Having trouble with the site? Describe it here and an administrator will pick it up.
      </p>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Your name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          What is going wrong?
        </label>
        <textarea
          value={issue}
          onChange={(e) => setIssue(e.target.value)}
          rows={6}
          placeholder="For example: attendance will not save for my class, or a student is missing from my roster."
          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent"
        />
      </div>

      {result && (
        <div className={`p-2.5 rounded-lg text-sm ${
          result.ok
            ? 'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-200 border border-green-300 dark:border-green-700'
            : 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border border-red-300 dark:border-red-700'
        }`}>
          {result.message}
        </div>
      )}

      <button
        onClick={submit}
        disabled={busy}
        className="w-full bg-[#7c1d1d] dark:bg-red-700 text-white px-4 py-2 rounded-lg hover:bg-[#651818] dark:hover:bg-red-800 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy ? 'Sending...' : 'Send to administrators'}
      </button>
    </div>
  );
}

function HelpDesk() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('open'); // open | resolved | all
  const [copied, setCopied] = useState(null); // ticket id, or "all"

  const load = async () => {
    setLoading(true);
    const { data, error: loadError } = await supabase
      .from('support_tickets')
      .select('*')
      .order('created_at', { ascending: false });
    if (loadError) {
      console.error('Failed to load help tickets:', loadError);
      setError(ticketErrorMessage(loadError));
      setTickets([]);
    } else {
      setError(null);
      setTickets(data || []);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const shown = tickets.filter((t) => filter === 'all' || t.status === filter);
  const openCount = tickets.filter((t) => t.status === 'open').length;

  // One ticket as a block that can be pasted straight into Claude Code.
  const ticketText = (t) => {
    const who = [t.reporter_name, t.reporter_title, t.reporter_class].filter(Boolean).join(' - ');
    return [
      `Ticket #${t.id} (${t.status})`,
      `From: ${who}${t.reporter_email ? ` <${t.reporter_email}>` : ''}`,
      `Filed: ${formatTicketTime(t.created_at)}`,
      '',
      t.issue,
    ].join('\n');
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Clipboard write failed:', err);
      window.prompt('Copy the text below:', text);
    }
  };

  const setStatus = async (t, status) => {
    const { error: upErr } = await supabase
      .from('support_tickets')
      .update({ status, resolved_at: status === 'resolved' ? new Date().toISOString() : null })
      .eq('id', t.id);
    if (upErr) { alert(`Could not update the ticket: ${upErr.message}`); return; }
    load();
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete ticket #${t.id} from ${t.reporter_name}? This cannot be undone.`)) return;
    const { error: delErr } = await supabase.from('support_tickets').delete().eq('id', t.id);
    if (delErr) { alert(`Could not delete the ticket: ${delErr.message}`); return; }
    load();
  };

  if (loading) {
    return <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">Loading tickets...</div>;
  }

  if (error) {
    return (
      <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-sm text-red-800 dark:text-red-200">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1">
          {['open', 'resolved', 'all'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-2.5 py-1 rounded text-xs font-medium capitalize transition-colors ${
                filter === f
                  ? 'bg-[#7c1d1d] text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              {f} {f === 'open' ? `(${openCount})` : ''}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="px-2.5 py-1 rounded text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            Refresh
          </button>
          <button
            onClick={() => copy(
              tickets.filter((t) => t.status === 'open').map(ticketText).join('\n\n---\n\n'),
              'all'
            )}
            disabled={openCount === 0}
            className="px-2.5 py-1 rounded text-xs font-medium bg-[#7c1d1d] dark:bg-red-700 text-white hover:bg-[#651818] dark:hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {copied === 'all' ? 'Copied' : `Copy all open (${openCount})`}
          </button>
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
          No {filter === 'all' ? '' : filter} tickets.
        </div>
      ) : (
        shown.map((t) => (
          <div
            key={t.id}
            className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 bg-gray-50 dark:bg-gray-700/40"
          >
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="text-sm">
                <span className="font-semibold text-gray-900 dark:text-white">{t.reporter_name}</span>
                {(t.reporter_title || t.reporter_class) && (
                  <span className="text-gray-600 dark:text-gray-400">
                    {' '}({[t.reporter_title, t.reporter_class].filter(Boolean).join(', ')})
                  </span>
                )}
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  #{t.id} - {formatTicketTime(t.created_at)}
                  {t.reporter_email ? ` - ${t.reporter_email}` : ''}
                </div>
              </div>
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                t.status === 'open'
                  ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300'
                  : 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
              }`}>
                {t.status}
              </span>
            </div>

            <p className="mt-2 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{t.issue}</p>

            <div className="mt-2 flex gap-2 flex-wrap">
              <button
                onClick={() => copy(ticketText(t), t.id)}
                className="px-2.5 py-1 rounded text-xs font-medium bg-[#7c1d1d] dark:bg-red-700 text-white hover:bg-[#651818] dark:hover:bg-red-800"
              >
                {copied === t.id ? 'Copied' : 'Copy for Claude'}
              </button>
              <button
                onClick={() => setStatus(t, t.status === 'open' ? 'resolved' : 'open')}
                className="px-2.5 py-1 rounded text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-500"
              >
                {t.status === 'open' ? 'Mark resolved' : 'Reopen'}
              </button>
              <button
                onClick={() => remove(t)}
                className="px-2.5 py-1 rounded text-xs font-medium text-red-600 dark:text-red-400 hover:underline"
              >
                Delete
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

/* =========================================================
   ARCHIVED STUDENTS (read-only history, admin only)
   ========================================================= */

function ArchivedStudents() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});
  // Holds the key currently exporting (a year, or "year||grade"), or null.
  const [pdfBusy, setPdfBusy] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const { data, error: loadError } = await supabase
        .from('students')
        .select('*')
        .eq('status', 'archived')
        .order('school_year', { ascending: false })
        .order('grade')
        .order('name')
        .limit(5000); // PostgREST caps at 1000 by default; archives accumulate.

      if (cancelled) return;
      if (loadError) {
        console.error('Error loading archived students:', loadError);
        setError(loadError.message);
      } else {
        setRows(data || []);
      }
      setLoading(false);
    }

    load();
    return () => { cancelled = true; };
  }, []);

  const query = search.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!query) return rows;
    return rows.filter((r) => (r.name || "").toLowerCase().includes(query));
  }, [rows, query]);

  // school_year -> grade -> students
  const byYear = useMemo(() => {
    const out = {};
    filtered.forEach((r) => {
      const year = r.school_year || "Unknown year";
      const grade = r.grade || "Unknown grade";
      if (!out[year]) out[year] = {};
      if (!out[year][grade]) out[year][grade] = [];
      out[year][grade].push(r);
    });
    return out;
  }, [filtered]);

  const years = Object.keys(byYear).sort().reverse();

  // Exports the full archived year, every grade, ordered grade then name --
  // not just whatever the current search happens to show.
  const exportPdf = async (key, title, subtitle, students, filename) => {
    setPdfBusy(key);
    try {
      await downloadRosterPdf({ title, subtitle, students, filename });
    } catch (err) {
      console.error("Failed to build roster PDF:", err);
      alert("Could not generate the PDF. Please try again.");
    } finally {
      setPdfBusy(null);
    }
  };

  const downloadYearPdf = (year) => {
    const all = rows
      .filter((r) => (r.school_year || "Unknown year") === year)
      .sort((a, b) =>
        (a.grade || "").localeCompare(b.grade || "") || (a.name || "").localeCompare(b.name || "")
      );
    const gradeCount = new Set(all.map((r) => r.grade)).size;
    return exportPdf(
      year,
      `${year} School Year Roster`,
      `MAPS Sunday School  ·  ${all.length} student${all.length === 1 ? "" : "s"}  ·  ${gradeCount} class${gradeCount === 1 ? "" : "es"}`,
      all,
      `${pdfSafeName(year)}-school-year-roster.pdf`
    );
  };

  const downloadArchivedGradePdf = (year, grade) => {
    const list = rows
      .filter((r) => (r.school_year || "Unknown year") === year && (r.grade || "Unknown grade") === grade)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return exportPdf(
      `${year}||${grade}`,
      `${grade} Roster`,
      `MAPS Sunday School ${year}  ·  ${list.length} student${list.length === 1 ? "" : "s"}`,
      list,
      `${pdfSafeName(grade)}-roster-${pdfSafeName(year)}.pdf`
    );
  };

  if (loading) {
    return <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">Loading archived students...</div>;
  }

  if (error) {
    return (
      <div className="text-center py-8 text-sm text-red-600 dark:text-red-400">
        Could not load archived students: {error}
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          If this mentions a missing column, the rollover migration has not been run yet.
        </p>
      </div>
    );
  }

  if (rows.length === 0) {
    return <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">No archived students yet.</div>;
  }

  return (
    <div>
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search archived students by name"
        className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 rounded-lg p-2.5 mb-4 text-sm focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent"
      />

      {filtered.length === 0 ? (
        <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
          No archived student matches "{search.trim()}".
        </div>
      ) : (
        years.map((year) => {
          const grades = Object.keys(byYear[year]).sort();
          const yearTotal = grades.reduce((n, g) => n + byYear[year][g].length, 0);

          return (
            <div key={year} className="mb-5">
              <div className="flex items-baseline justify-between mb-2 gap-3">
                <h4 className="text-sm font-bold text-[#7c1d1d] dark:text-red-400">{year}</h4>
                <div className="flex items-baseline gap-3">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {yearTotal} student{yearTotal === 1 ? "" : "s"} across {grades.length} grade{grades.length === 1 ? "" : "s"}
                  </span>
                  <button
                    onClick={() => downloadYearPdf(year)}
                    disabled={pdfBusy === year}
                    className="bg-[#7c1d1d] dark:bg-red-700 text-white px-2.5 py-1 rounded text-xs font-medium hover:bg-[#651818] dark:hover:bg-red-800 transition-colors disabled:opacity-50 whitespace-nowrap"
                  >
                    {pdfBusy === year ? "Preparing..." : "⬇ Download PDF"}
                  </button>
                </div>
              </div>

              <div className="space-y-1">
                {grades.map((grade) => {
                  const key = `${year}||${grade}`;
                  const students = byYear[year][grade];
                  // A search implies the user wants to see the hits, not hunt for them.
                  const isOpen = query ? true : !!expanded[key];

                  return (
                    <div key={key} className="border dark:border-gray-600 rounded">
                      {/* Toggle and download are siblings: a button cannot nest inside a button. */}
                      <div className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                        <button
                          onClick={() => setExpanded((e) => ({ ...e, [key]: !e[key] }))}
                          className="flex-1 flex items-center justify-between text-left"
                        >
                          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                            {isOpen ? "▾" : "▸"} {grade}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 mr-3">{students.length}</span>
                        </button>
                        <button
                          onClick={() => downloadArchivedGradePdf(year, grade)}
                          disabled={pdfBusy === key}
                          title={`Download ${grade} roster as PDF`}
                          className="text-xs text-[#7c1d1d] dark:text-red-400 hover:underline disabled:opacity-50 whitespace-nowrap"
                        >
                          {pdfBusy === key ? "..." : "⬇ PDF"}
                        </button>
                      </div>

                      {isOpen && (
                        <div className="border-t dark:border-gray-600 divide-y dark:divide-gray-700">
                          {students.map((s) => {
                            const p = s.parent_info || {};
                            const contact = [p.name, p.phone, p.email].filter(Boolean).join(" · ");
                            return (
                              <div key={s.id} className="px-3 py-2">
                                <div className="text-sm text-gray-800 dark:text-gray-200">{s.name}</div>
                                {contact && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{contact}</div>
                                )}
                                {p.allergies && (
                                  <div className="text-xs text-red-700 dark:text-red-400 mt-0.5">
                                    Allergies: {p.allergies}
                                  </div>
                                )}
                                {p.medicalConditions && (
                                  <div className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                                    Medical: {p.medicalConditions}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

/* =========================================================
   HEADER
   ========================================================= */

function Header({ user, onLogout, activeSection, setActiveSection, onOpenAccountSettings }) {
  return (
    <header className="bg-[#7c1d1d] dark:bg-gray-900 text-white shadow-lg transition-colors duration-200">
      <div className="w-full mx-auto px-2 md:px-3 lg:px-4 py-1.5 md:py-2 flex items-center justify-between gap-1.5 md:gap-2 lg:gap-2.5">
        <div className="flex items-center gap-1.5 md:gap-2 lg:gap-2.5 flex-shrink">
          <div className="w-[28px] h-[28px] md:w-[32px] md:h-[32px] lg:w-[36px] lg:h-[36px] xl:w-[42px] xl:h-[42px] flex items-center justify-center flex-shrink-0">
            <img
              src="/logo.png"
              alt="MAPS Logo"
              className="w-full h-full object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <span className="hidden text-3xl md:text-4xl">🕌</span>
          </div>
          <div className="flex flex-col justify-center min-w-0 hidden lg:block">
            <h1 className="text-xs lg:text-sm xl:text-base font-bold leading-tight whitespace-nowrap">
              MAPS Sunday School
            </h1>
            <p className="text-[9px] lg:text-[10px] xl:text-xs text-white/90 whitespace-nowrap">Logistics Management</p>
          </div>
        </div>

        {user && user.status !== "pending" ? (
          <>
            <nav className="hidden md:flex gap-1 lg:gap-1.5 bg-white/10 rounded-lg p-1 overflow-x-auto flex-shrink min-w-0">
              <HeaderTab
                label="Calendar"
                active={activeSection === "calendar"}
                onClick={() => setActiveSection("calendar")}
              />
              <HeaderTab
                label="Attendance"
                active={activeSection === "attendance"}
                onClick={() => setActiveSection("attendance")}
              />
              <HeaderTab
                label={user.role === "admin" ? "Roster" : "Students"}
                active={activeSection === "students"}
                onClick={() => setActiveSection("students")}
              />
              <HeaderTab
                label="Resources"
                active={activeSection === "resources"}
                onClick={() => setActiveSection("resources")}
              />
              <HeaderTab
                label="Announcements"
                active={activeSection === "announcements"}
                onClick={() => setActiveSection("announcements")}
              />
              <HeaderTab
                label="Print"
                active={activeSection === "print"}
                onClick={() => setActiveSection("print")}
              />
              {canApproveUsers(user) && (
                <HeaderTab
                  label="Approvals"
                  active={activeSection === "approvals"}
                  onClick={() => setActiveSection("approvals")}
                />
              )}
            </nav>
            <div className="flex items-center gap-1.5 lg:gap-2 flex-shrink">
              <div className="hidden 2xl:block text-right">
                <div className="text-xs font-medium">{user.name}</div>
                <div className="text-[10px] text-white/70">{user.title || user.role}</div>
              </div>
              <button
                className="bg-white/20 text-white px-1.5 md:px-2 py-1 md:py-1.5 rounded font-medium hover:bg-white/30 text-xs transition-colors flex-shrink-0"
                onClick={onOpenAccountSettings}
                title="Account Settings"
              >
                ⚙️
              </button>
            </div>
          </>
        ) : null}
      </div>

      {user && user.status !== "pending" ? (
        <div className="md:hidden px-4 pb-3 flex gap-2 overflow-x-auto">
          <button
            onClick={() => setActiveSection("calendar")}
            className={`flex-1 px-3 py-2 rounded font-medium transition-colors whitespace-nowrap ${
              activeSection === "calendar"
                ? "bg-white text-[#7c1d1d]"
                : "bg-white/10 text-white hover:bg-white/20"
            }`}
          >
            Calendar
          </button>
          <button
            onClick={() => setActiveSection("attendance")}
            className={`flex-1 px-3 py-2 rounded font-medium transition-colors whitespace-nowrap ${
              activeSection === "attendance"
                ? "bg-white text-[#7c1d1d]"
                : "bg-white/10 text-white hover:bg-white/20"
            }`}
          >
            Attendance
          </button>
          <button
            onClick={() => setActiveSection("students")}
            className={`flex-1 px-3 py-2 rounded font-medium transition-colors whitespace-nowrap ${
              activeSection === "students"
                ? "bg-white text-[#7c1d1d]"
                : "bg-white/10 text-white hover:bg-white/20"
            }`}
          >
            {user.role === "admin" ? "Roster" : "Students"}
          </button>
          <button
            onClick={() => setActiveSection("resources")}
            className={`flex-1 px-3 py-2 rounded font-medium transition-colors whitespace-nowrap ${
              activeSection === "resources"
                ? "bg-white text-[#7c1d1d]"
                : "bg-white/10 text-white hover:bg-white/20"
            }`}
          >
            Resources
          </button>
          <button
            onClick={() => setActiveSection("announcements")}
            className={`flex-1 px-3 py-2 rounded font-medium transition-colors whitespace-nowrap ${
              activeSection === "announcements"
                ? "bg-white text-[#7c1d1d]"
                : "bg-white/10 text-white hover:bg-white/20"
            }`}
          >
            News
          </button>
          <button
            onClick={() => setActiveSection("print")}
            className={`flex-1 px-3 py-2 rounded font-medium transition-colors whitespace-nowrap ${
              activeSection === "print"
                ? "bg-white text-[#7c1d1d]"
                : "bg-white/10 text-white hover:bg-white/20"
            }`}
          >
            Print
          </button>
          {canApproveUsers(user) && (
            <button
              onClick={() => setActiveSection("approvals")}
              className={`flex-1 px-3 py-2 rounded font-medium transition-colors whitespace-nowrap ${
                activeSection === "approvals"
                  ? "bg-white text-[#7c1d1d]"
                  : "bg-white/10 text-white hover:bg-white/20"
              }`}
            >
              Approvals
            </button>
          )}
        </div>
      ) : null}
    </header>
  );
}

function HeaderTab({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-2 md:px-2.5 lg:px-3 py-1 md:py-1.5 rounded text-[10px] md:text-[11px] lg:text-xs font-medium transition-colors whitespace-nowrap ${
        active ? "bg-white dark:bg-gray-700 text-[#7c1d1d] dark:text-red-400 shadow-sm" : "text-white/90 hover:bg-white/10"
      }`}
    >
      {label}
    </button>
  );
}

/* =========================================================
   AUTH (LOGIN / TEACHER SIGN UP)
   ========================================================= */

function Auth({ state, setState }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [gradeLevel, setGradeLevel] = useState("");
  const [teacherRole, setTeacherRole] = useState("");
  const [isTeamLead, setIsTeamLead] = useState(false);
  const [teamLeadGrade, setTeamLeadGrade] = useState("");
  const [requestAdminControl, setRequestAdminControl] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetAnswer, setResetAnswer] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetUser, setResetUser] = useState(null);

  // Signup verification
  const [signupVerificationCode, setSignupVerificationCode] = useState("");
  const [signupVerificationSent, setSignupVerificationSent] = useState(false);
  const [signupVerificationInput, setSignupVerificationInput] = useState("");
  const [pendingSignupData, setPendingSignupData] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    const loginInput = email.trim().toLowerCase();

    try {
      // Query Supabase directly for the most up-to-date user data
      const { data: users, error } = await supabase
        .from('users')
        .select('*')
        .or(`email.ilike.${loginInput},username.ilike.${loginInput}`)
        .limit(1);

      if (error) {
        console.error('❌ Error querying Supabase:', error);
        alert("Error logging in. Please try again.");
        return;
      }

      if (!users || users.length === 0) {
        console.log("❌ User not found in database");
        alert("Invalid email/username or password");
        return;
      }

      const found = users[0];
      console.log("✅ User found in Supabase:", { email: found.email, role: found.role, status: found.status });

      // Check if user is pending approval
      if (found.status === 'pending') {
        alert("Your account is pending approval from an administrator. Please wait for approval before logging in.");
        return;
      }

      // Verify password
      if (!(await verifyPassword(password, found.password))) {
        console.log("❌ Password mismatch");
        alert("Invalid email/username or password");
        return;
      }

      // The password is known good, so a row still holding it in plaintext
      // can be rewritten as a hash right now. Failing here must not block the
      // login: the account is simply upgraded on a later sign-in instead.
      if (!isHashedPassword(found.password)) {
        try {
          const upgraded = await hashPassword(password);
          await supabase.from('users').update({ password: upgraded }).eq('email', found.email);
          console.log('🔒 Stored password upgraded to a hash');
        } catch (err) {
          console.warn('Could not upgrade the stored password:', err);
        }
      }

      console.log("✅ Login successful");
      rememberSession(found.email);

      setState((s) => ({
        ...s,
        currentUser: {
          email: found.email,
          role: found.role,
          name: found.name,
          title: found.title,
          gradeLevel: found.grade_level,
          isTeamLead: found.is_team_lead || false,
          teamLeadGrade: found.team_lead_grade || null,
          status: found.status || "approved"
        },
      }));
    } catch (err) {
      console.error('❌ Login error:', err);
      alert("Error logging in. Please try again.");
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();

    // Check for duplicate email in Supabase
    const { data: existingUsers, error: emailCheckError } = await supabase
      .from('users')
      .select('email, status')
      .ilike('email', email.trim());

    if (emailCheckError) {
      console.error('Error checking email:', emailCheckError);
    }

    if (existingUsers && existingUsers.length > 0) {
      const existingUser = existingUsers[0];

      // If user was denied, delete the old record so they can try again
      if (existingUser.status === 'denied') {
        console.log('🔄 User was previously denied, removing old record to allow re-signup');
        const { error: deleteError } = await supabase
          .from('users')
          .delete()
          .eq('email', email.trim());

        if (deleteError) {
          console.error('Error deleting denied user:', deleteError);
          alert('Error processing signup. Please try again.');
          return;
        }

        console.log('✅ Old denied record deleted, proceeding with signup');
      } else {
        // User is approved or pending - cannot sign up again
        alert("That email already has an account or a pending request.");
        return;
      }
    }

    // Check for duplicate username in Supabase
    const { data: existingUsernames, error: usernameCheckError } = await supabase
      .from('users')
      .select('username, status, email')
      .ilike('username', username.trim());

    if (usernameCheckError) {
      console.error('Error checking username:', usernameCheckError);
    }

    if (existingUsernames && existingUsernames.length > 0) {
      const existingUsernameUser = existingUsernames[0];

      // If the username belongs to a denied user, it's okay (we'll delete that user anyway)
      if (existingUsernameUser.status !== 'denied') {
        alert("That username is already taken. Please choose a different one.");
        return;
      }
    }

    if (!username.trim()) {
      alert("Please enter a username.");
      return;
    }

    if (!gradeLevel) {
      alert("Please select your grade level.");
      return;
    }

    if (!teacherRole) {
      alert("Please select your role (Admin, Teacher, or Teacher Assistant).");
      return;
    }

    if (isTeamLead && !teamLeadGrade) {
      alert("Please select which grade you are a team lead for.");
      return;
    }

    // Generate verification code and send email
    const verificationCode = Math.floor(100000 + Math.random() * 900000).toString();

    const result = await sendVerificationCode(email.trim(), name.trim(), verificationCode, true);

    if (result.success) {
      console.log("✅ Verification code sent successfully");
      alert(`A verification code has been sent to ${email.trim()}. Please check your email.`);

      // Store signup data and verification code
      setSignupVerificationCode(verificationCode);
      setSignupVerificationSent(true);
      setPendingSignupData({
        email: email.trim(),
        password,
        name: name.trim(),
        username: username.trim().toLowerCase(),
        gradeLevel,
        teacherRole,
        isTeamLead,
        teamLeadGrade: isTeamLead ? teamLeadGrade : null,
        requestAdminControl,
      });
    } else {
      console.error("❌ Failed to send verification email:", result.error);
      // A mail-service fault is not the applicant's fault; say so rather than
      // sending them to re-check an address that was never the problem.
      const serviceProblem = /ip restriction|api key|email service|could not reach/i.test(
        result.serverMessage || result.error || ''
      );
      alert(
        serviceProblem
          ? "We could not send your verification code because of a problem with our email service. Please contact an administrator - this is not a problem with your email address."
          : "Failed to send verification code. Please check your email address and try again."
      );
    }
  };

  const handleVerifySignup = async (e) => {
    e.preventDefault();

    // Verify the code matches
    if (signupVerificationInput.trim() !== signupVerificationCode) {
      alert("Incorrect verification code. Please check your email and try again.");
      return;
    }

    let hashedSignupPassword;
    try {
      hashedSignupPassword = await hashPassword(pendingSignupData.password);
    } catch (err) {
      console.error('Could not hash the new password:', err);
      alert('Could not create your account securely. Please try again.');
      return;
    }

    // Create new user directly in users table with status='pending'
    const newUser = {
      email: pendingSignupData.email,
      username: pendingSignupData.username,
      password: hashedSignupPassword,
      name: pendingSignupData.name,
      grade_level: pendingSignupData.gradeLevel || "N/A",  // Default to N/A if empty (for admins)
      title: pendingSignupData.teacherRole,
      is_team_lead: pendingSignupData.isTeamLead,
      team_lead_grade: pendingSignupData.teamLeadGrade,
      role: "teacher",
      status: "pending"  // Key change: status field instead of separate table
    };

    // Save directly to users table in Supabase
    try {
      const { data, error } = await supabase
        .from('users')
        .insert([newUser])
        .select();

      if (error) {
        console.error('❌ Error saving user:', error);
        alert('Error creating account. Please try again.');
        return;
      }

      console.log('✅ User saved with pending status:', data);
    } catch (err) {
      console.error('❌ Error:', err);
      alert('Error creating account. Please try again.');
      return;
    }

    // Send notification email to Haya via Brevo
    fetch('/api/send-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to: 'haya.awadallah1@gmail.com',
        toName: 'Haya Awadallah',
        subject: `New Teacher Signup Request: ${pendingSignupData.name}`,
        htmlContent: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2 style="color: #7c1d1d;">MAPS Sunday School - New Teacher Signup Request</h2>
            <p>A new teacher has signed up and is awaiting your approval:</p>
            <div style="background-color: #f5f5f5; padding: 15px; border-radius: 8px; margin: 20px 0;">
              <p><strong>Name:</strong> ${pendingSignupData.name}</p>
              <p><strong>Email:</strong> ${pendingSignupData.email}</p>
              <p><strong>Role:</strong> ${pendingSignupData.teacherRole}</p>
              <p><strong>Grade Level:</strong> ${pendingSignupData.gradeLevel}</p>
              ${pendingSignupData.isTeamLead ? `<p><strong>Team Lead For:</strong> ${pendingSignupData.teamLeadGrade}</p>` : ''}
              ${pendingSignupData.requestAdminControl ? '<p><strong>Requesting Admin Access:</strong> Yes</p>' : ''}
              <p><strong>Request Date:</strong> ${new Date().toLocaleString()}</p>
            </div>
            <p>Please log in to the Sunday School app to approve or deny this request.</p>
            <hr style="margin: 20px 0; border: none; border-top: 1px solid #ccc;">
            <p style="color: #666; font-size: 12px;">
              MAPS Sunday School Logistics Management System
            </p>
          </div>
        `
      })
    }).then(
      (response) => {
        if (response.ok) {
          console.log("✅ Email sent successfully to admin");
          alert(`Thank you ${pendingSignupData.name}! Your email has been verified and your request has been sent to the admin for approval.`);
        } else {
          throw new Error('Failed to send email');
        }
      }
    ).catch(
      (error) => {
        console.error("❌ Failed to send email:", error);
        alert(`Thank you ${pendingSignupData.name}! Your email has been verified. Please contact the admin for approval.`);
      }
    );

    // Reset all form fields
    setMode("login");
    setEmail("");
    setPassword("");
    setName("");
    setGradeLevel("");
    setTeacherRole("");
    setIsTeamLead(false);
    setTeamLeadGrade("");
    setRequestAdminControl(false);
    setSignupVerificationSent(false);
    setSignupVerificationInput("");
    setSignupVerificationCode("");
    setPendingSignupData(null);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    const input = resetEmail.trim().toLowerCase();
    if (!input) return;

    // Query Supabase rather than state.users: the in-memory list is loaded
    // once at startup and can be stale or empty, which made this report
    // "No account found" for accounts that do exist. Login already does this.
    let user;
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .or(`email.ilike.${input},username.ilike.${input}`)
        .limit(1);
      if (error) throw error;
      user = data && data[0];
    } catch (err) {
      console.error('❌ Error looking up account:', err);
      alert('Could not reach the server. Please try again.');
      return;
    }

    if (!user) {
      alert("No account found with that email address.");
      return;
    }

    const verificationCode = Math.floor(100000 + Math.random() * 900000).toString();
    const result = await sendVerificationCode(user.email, user.name, verificationCode);

    if (result.success) {
      console.log("✅ Verification code sent successfully");
      alert(`A verification code has been sent to ${user.email}. Please check your email.`);
      setResetUser({ ...user, verificationCode });
    } else {
      console.error("❌ Failed to send verification email:", result.error);
      // Distinguish "our mail service is broken" from anything the person
      // typed, so they stop retrying an address that was never the problem.
      const configProblem = /api key|unauthorized|401|not enabled/i.test(result.error || '');
      alert(
        configProblem
          ? "Password reset email is temporarily unavailable because of a mail service problem on our end. Please contact an administrator to have your password reset."
          : "Failed to send verification code. Please try again or contact the administrator."
      );
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (resetAnswer.trim() !== resetUser.verificationCode) {
      alert("Incorrect verification code. Please check your email and try again.");
      return;
    }

    if (newPassword !== confirmPassword) {
      alert("Passwords do not match!");
      return;
    }

    if (newPassword.length < 4) {
      alert("Password must be at least 4 characters long.");
      return;
    }

    let hashedReset;
    try {
      hashedReset = await hashPassword(newPassword);
    } catch (err) {
      console.error('Could not hash the new password:', err);
      alert('Could not set your new password securely. Please try again.');
      return;
    }

    // Persist to Supabase. This previously updated React state only, so the
    // app reported success and the old password kept working.
    const { error } = await supabase
      .from('users')
      .update({ password: hashedReset })
      .eq('email', resetUser.email);

    if (error) {
      console.error('❌ Failed to save new password:', error);
      alert('Could not save your new password. Please try again or contact an administrator.');
      return;
    }

    setState((s) => ({
      ...s,
      users: s.users.map((u) =>
        u.email === resetUser.email
          ? { ...u, password: hashedReset }
          : u
      ),
    }));

    alert("Password reset successfully! You can now log in with your new password.");
    setMode("login");
    setResetEmail("");
    setResetAnswer("");
    setNewPassword("");
    setConfirmPassword("");
    setResetUser(null);
  };

  // Forgot Password UI
  if (mode === "forgot") {
    if (!resetUser) {
      return (
        <div className="max-w-md mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mt-6 border-t-4 border-[#7c1d1d] dark:border-red-600">
          <h2 className="text-2xl font-bold text-[#7c1d1d] dark:text-red-400 mb-1">Forgot Password</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            Enter your email to begin password recovery
          </p>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
              <input
                type="email"
                required
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="mt-1 w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="you@example.com"
              />
            </div>
            <button className="w-full bg-[#7c1d1d] text-white px-4 py-2.5 rounded-lg hover:bg-[#651818] font-medium transition-colors">
              Continue
            </button>
          </form>
          <p className="text-sm text-center text-gray-600 dark:text-gray-300 mt-4">
            Remember your password?{" "}
            <button
              onClick={() => {
                setMode("login");
                setResetEmail("");
              }}
              className="text-[#7c1d1d] dark:text-red-400 hover:underline font-medium"
            >
              Back to Login
            </button>
          </p>
        </div>
      );
    } else {
      return (
        <div className="max-w-md mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mt-6 border-t-4 border-[#7c1d1d] dark:border-red-600">
          <h2 className="text-2xl font-bold text-[#7c1d1d] dark:text-red-400 mb-1">Reset Password</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            Enter the verification code sent to your email
          </p>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/30 p-3 rounded-lg border border-blue-200 dark:border-blue-700">
              <p className="text-sm text-blue-900 dark:text-blue-200">
                A 6-digit verification code has been sent to <strong>{resetEmail}</strong>
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Verification Code</label>
              <input
                type="text"
                required
                value={resetAnswer}
                onChange={(e) => setResetAnswer(e.target.value)}
                className="mt-1 w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent text-center text-lg tracking-widest dark:bg-gray-700 dark:text-white"
                placeholder="000000"
                maxLength={6}
                pattern="[0-9]{6}"
                autoComplete="off"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Check your email for the 6-digit code</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">New Password</label>
              <input
                type="password"
                required
                minLength={4}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-1 w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Confirm New Password</label>
              <input
                type="password"
                required
                minLength={4}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1 w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="••••••••"
                autoComplete="new-password"
              />
            </div>
            <button className="w-full bg-[#7c1d1d] text-white px-4 py-2.5 rounded-lg hover:bg-[#651818] font-medium transition-colors">
              Reset Password
            </button>
          </form>
          <p className="text-sm text-center text-gray-600 dark:text-gray-300 mt-4">
            <button
              onClick={() => {
                setMode("login");
                setResetUser(null);
                setResetEmail("");
                setResetAnswer("");
                setNewPassword("");
                setConfirmPassword("");
              }}
              className="text-[#7c1d1d] dark:text-red-400 hover:underline font-medium"
            >
              Back to Login
            </button>
          </p>
        </div>
      );
    }
  }

  // Signup email verification UI
  if (signupVerificationSent && mode === "signup") {
    return (
      <div className="max-w-md mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mt-6 border-t-4 border-[#7c1d1d] dark:border-red-600">
        <h2 className="text-2xl font-bold text-[#7c1d1d] dark:text-red-400 mb-1">Verify Your Email</h2>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Enter the verification code sent to your email
        </p>
        <form onSubmit={handleVerifySignup} className="space-y-4">
          <div className="bg-blue-50 dark:bg-blue-900/30 p-3 rounded-lg border border-blue-200 dark:border-blue-700">
            <p className="text-sm text-blue-900 dark:text-blue-200">
              A 6-digit verification code has been sent to <strong>{pendingSignupData?.email}</strong>
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Verification Code</label>
            <input
              type="text"
              required
              value={signupVerificationInput}
              onChange={(e) => setSignupVerificationInput(e.target.value)}
              className="mt-1 w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent text-center text-lg tracking-widest dark:bg-gray-700 dark:text-white"
              placeholder="000000"
              maxLength={6}
              pattern="[0-9]{6}"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Check your email for the 6-digit code</p>
          </div>
          <button className="w-full bg-[#7c1d1d] text-white px-4 py-2.5 rounded-lg hover:bg-[#651818] font-medium transition-colors">
            Verify Email
          </button>
        </form>
        <p className="text-sm text-center text-gray-600 dark:text-gray-300 mt-4">
          <button
            onClick={() => {
              setSignupVerificationSent(false);
              setSignupVerificationInput("");
              setSignupVerificationCode("");
              setPendingSignupData(null);
            }}
            className="text-[#7c1d1d] dark:text-red-400 hover:underline font-medium"
          >
            Back to Sign Up
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mt-6 border-t-4 border-[#7c1d1d] dark:border-red-600">
      <h2 className="text-2xl font-bold text-[#7c1d1d] dark:text-red-400 mb-1">
        {mode === "login" ? "Login" : "Teacher Sign Up"}
      </h2>
      {mode === "login" ? (
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Enter your credentials to access the system
        </p>
      ) : (
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Submit your information for approval by the secretary
        </p>
      )}

      <form
        onSubmit={mode === "login" ? handleLogin : handleSignup}
        className="space-y-4"
      >
        {mode === "signup" && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Full Name</label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Your full name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Username</label>
              <input
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/\s/g, ''))}
                className="mt-1 w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="username (no spaces)"
              />
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                You can use this to log in instead of your email
              </p>
            </div>
          </>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {mode === "login" ? "Email or Username" : "Email"}
          </label>
          <input
            type={mode === "login" ? "text" : "email"}
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-700 dark:text-white"
            placeholder={mode === "login" ? "Email or username" : "you@example.com"}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              required
              minLength={4}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 pr-10 focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-700 dark:text-white"
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2 top-1/2 -translate-y-1/2 mt-0.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
        </div>
        {mode === "signup" && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
              <select
                required
                value={teacherRole}
                onChange={(e) => setTeacherRole(e.target.value)}
                className="mt-1 w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="">Select your role</option>
                <option value="Admin">Admin</option>
                <option value="Teacher">Teacher</option>
                <option value="Teacher Assistant">Teacher Assistant</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Grade Level Teaching</label>
              <select
                required={teacherRole !== "Admin"}
                value={gradeLevel}
                onChange={(e) => setGradeLevel(e.target.value)}
                className="mt-1 w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="">Select your grade level</option>
                {teacherRole === "Admin" && <option value="N/A">N/A (Admin only)</option>}
                {ALL_GRADES.map((grade) => (
                  <option key={grade} value={grade}>
                    {grade}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
              <input
                type="checkbox"
                id="teamLead"
                checked={isTeamLead}
                onChange={(e) => setIsTeamLead(e.target.checked)}
                className="mt-1 w-4 h-4 text-[#7c1d1d] border-gray-300 dark:border-gray-600 rounded focus:ring-[#7c1d1d]"
              />
              <label htmlFor="teamLead" className="text-sm text-gray-700 dark:text-gray-300">
                <span className="font-semibold">I am a Team Lead</span>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Check this if you lead a specific grade level team
                </p>
              </label>
            </div>
            {isTeamLead && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Team Lead For Grade</label>
                <select
                  required
                  value={teamLeadGrade}
                  onChange={(e) => setTeamLeadGrade(e.target.value)}
                  className="mt-1 w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select grade you lead</option>
                  {TEAM_LEAD_GRADES.map((grade) => (
                    <option key={grade} value={grade}>
                      {grade}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg">
              <input
                type="checkbox"
                id="adminControl"
                checked={requestAdminControl}
                onChange={(e) => setRequestAdminControl(e.target.checked)}
                className="mt-1 w-4 h-4 text-[#7c1d1d] border-gray-300 dark:border-gray-600 rounded focus:ring-[#7c1d1d]"
              />
              <label htmlFor="adminControl" className="text-sm text-gray-700 dark:text-gray-300">
                <span className="font-semibold">Request Admin Control</span>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Check this box if you need administrative access (approval required)
                </p>
              </label>
            </div>
          </>
        )}
        <button className="w-full bg-[#7c1d1d] text-white px-4 py-2.5 rounded-lg hover:bg-[#651818] font-medium transition-colors">
          {mode === "login" ? "Login" : "Submit for Approval"}
        </button>
      </form>

      <div className="mt-4 text-sm text-center space-y-2">
        {mode === "login" && (
          <div>
            <button
              onClick={() => setMode("forgot")}
              className="text-[#7c1d1d] dark:text-red-400 hover:underline font-medium"
            >
              Forgot Password?
            </button>
          </div>
        )}
        {mode === "login" ? (
          <button
            onClick={() => setMode("signup")}
            className="text-[#7c1d1d] dark:text-red-400 hover:underline font-medium"
          >
            Need an account? Teacher Sign Up
          </button>
        ) : (
          <button
            onClick={() => setMode("login")}
            className="text-[#7c1d1d] dark:text-red-400 hover:underline font-medium"
          >
            Have an account? Log in
          </button>
        )}
      </div>
    </div>
  );
}

/* =========================================================
   PENDING APPROVAL SCREEN
   ========================================================= */

function PendingApproval() {
  return (
    <div className="max-w-md mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mt-6 border-t-4 border-yellow-500 dark:border-yellow-600">
      <div className="text-center">
        <div className="text-6xl mb-4">⏳</div>
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">
          Approval Pending
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mb-4">
          Your account is awaiting approval from the admin.
          You will be notified once your account is approved.
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          If you have questions, please contact the admin.
        </p>
      </div>
    </div>
  );
}

/* =========================================================
   APPROVALS (Admin Only)
   ========================================================= */

function Approvals({ state, setState }) {
  const [pendingUsers, setPendingUsers] = useState([]);
  // Only the very first load blocks the view. Background polls and manual
  // refreshes update the list in place, so the spinner does not reappear
  // every few seconds and yank the list out from under whoever is reading it.
  const [firstLoad, setFirstLoad] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async (silent) => {
      if (!silent) setRefreshing(true);
      try {
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('status', 'pending')
          .order('created_at', { ascending: false });
        if (cancelled) return;
        if (error) {
          console.error('❌ Error fetching pending users:', error);
          return;
        }
        setPendingUsers(data || []);
      } catch (err) {
        if (!cancelled) console.error('❌ Error:', err);
      } finally {
        if (!cancelled) {
          setFirstLoad(false);
          if (!silent) setRefreshing(false);
        }
      }
    };

    load(false);
    const interval = setInterval(() => load(true), 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Manual refresh: keeps the list on screen and only changes the button label.
  const fetchPendingUsers = async () => {
    setRefreshing(true);
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      if (error) {
        console.error('❌ Error fetching pending users:', error);
        return;
      }
      setPendingUsers(data || []);
    } catch (err) {
      console.error('❌ Error:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const approveUser = async (userEmail, grantAdminControl = false) => {
    console.log("🔄 Approving user:", userEmail, "Grant admin:", grantAdminControl);

    try {
      // Simply update the status field to 'approved' and optionally change role
      const updates = {
        status: 'approved',
        role: grantAdminControl ? 'admin' : 'teacher'
      };

      const { error } = await supabase
        .from('users')
        .update(updates)
        .eq('email', userEmail);

      if (error) {
        console.error('❌ Error approving user:', error);
        alert('Error approving user: ' + error.message);
        return;
      }

      console.log('✅ User approved successfully');
      alert(`User approved successfully${grantAdminControl ? " with admin privileges" : ""}!`);

      // Refresh the pending users list
      fetchPendingUsers();
    } catch (err) {
      console.error('❌ Error:', err);
      alert('Error: ' + err.message);
    }
  };

  const denyUser = async (userEmail) => {
    if (!window.confirm("Are you sure you want to deny this request?")) return;

    try {
      // Simply update the status field to 'denied'
      const { error } = await supabase
        .from('users')
        .update({ status: 'denied' })
        .eq('email', userEmail);

      if (error) {
        console.error('❌ Error denying user:', error);
        alert('Error denying user: ' + error.message);
        return;
      }

      console.log('✅ User denied');
      alert("User request denied.");

      // Refresh the pending users list
      fetchPendingUsers();
    } catch (err) {
      console.error('❌ Error:', err);
      alert('Error: ' + err.message);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 md:p-4 border-t-4 border-[#7c1d1d] dark:border-red-600">
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-base font-semibold text-[#7c1d1d] dark:text-red-400">
          Pending Teacher Approvals
        </h2>
        <button
          onClick={fetchPendingUsers}
          className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
          disabled={refreshing}
        >
          {refreshing ? "Refreshing..." : "🔄 Refresh"}
        </button>
      </div>

      {firstLoad ? (
        <div className="text-center py-4 text-gray-500 dark:text-gray-400">
          <div className="text-2xl mb-1">⏳</div>
          <p className="text-xs">Loading pending approvals...</p>
        </div>
      ) : pendingUsers.length === 0 ? (
        <div className="text-center py-4 text-gray-500 dark:text-gray-400">
          <div className="text-2xl mb-1">✅</div>
          <p className="text-xs">No pending approval requests (Auto-refresh: ON)</p>
        </div>
      ) : (
        <div className="space-y-2">
          {pendingUsers.map((user) => (
            <div
              key={user.id}
              className="border dark:border-gray-600 rounded p-2 flex items-center justify-between bg-yellow-50 dark:bg-yellow-900/20"
            >
              <div>
                <h3 className="font-semibold text-xs text-gray-800 dark:text-gray-200">{user.name}</h3>
                <p className="text-[10px] text-gray-600 dark:text-gray-300">{user.email}</p>
                <p className="text-[10px] text-gray-600 dark:text-gray-300">Role: {user.title || 'Teacher'}</p>
                <p className="text-[10px] text-gray-600 dark:text-gray-300">Grade: {user.grade_level || 'Not specified'}</p>
                {user.is_team_lead && (
                  <p className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 mt-0.5">
                    👥 Team Lead for {user.team_lead_grade}
                  </p>
                )}
                <p className="text-[9px] text-gray-500 dark:text-gray-400 mt-0.5">
                  Requested: {user.created_at ? new Date(user.created_at).toLocaleString() : 'Unknown'}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                {false ? (
                  <>
                    <button
                      onClick={() => approveUser(user.email, true)}
                      className="bg-[#7c1d1d] text-white px-2 py-1 rounded text-xs hover:bg-[#651818] font-medium transition-colors whitespace-nowrap"
                    >
                      Approve as Admin
                    </button>
                    <button
                      onClick={() => approveUser(user.email, false)}
                      className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700 font-medium transition-colors whitespace-nowrap"
                    >
                      Approve as Teacher
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => approveUser(user.email)}
                    className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700 font-medium transition-colors"
                  >
                    Approve
                  </button>
                )}
                <button
                  onClick={() => denyUser(user.email)}
                  className="bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700 font-medium transition-colors"
                >
                  Deny
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* =========================================================
   DAILY SCHEDULES

   Two schedules, because the prayer slots move when the clocks do.
   Under daylight saving Dhuhr falls after class and sits at the end of
   the session; under standard time it lands mid-session, and the PM
   session gains a Maghrib slot that the daylight saving one does not
   have. Both are transcribed from the official AM/PM schedule sheets,
   not derived from each other, so neither drifts if one is corrected.
   ========================================================= */

const SCHEDULE_DST = {
  AM: [
    { period: "0",  time: "10:00-10:15 AM", duration: "15 minutes", activity: "Staff Meeting" },
    { period: "0+", time: "10:15-10:30",    duration: "15 minutes", activity: "Assembly" },
    { period: "1",  time: "10:30-11:25 AM", duration: "55 minutes", activity: "Islamic Studies" },
    { period: "2",  time: "11:30-11:45 PM", duration: "15 minutes", activity: "Lunch" },
    { period: "3",  time: "11:45-12:15 PM", duration: "30 minutes", activity: "Recess" },
    { period: "4",  time: "12:20-1:15 PM",  duration: "55 minutes", activity: "Quran" },
    { period: "5",  time: "1:30-1:45 PM",   duration: "15 minutes", activity: "Dhuhr" },
    { period: "",   time: "1:45 PM",        duration: "Dismissal",  activity: "" },
  ],
  PM: [
    { period: "0",  time: "2:00-2:15pm", duration: "15 minutes", activity: "Staff Meeting" },
    { period: "0+", time: "2:15-2:30pm", duration: "15 minutes", activity: "Assembly" },
    { period: "1",  time: "2:30-3:30 pm", duration: "60 minutes", activity: "Islamic Studies" },
    { period: "2",  time: "3:30-3:45 pm", duration: "15 minutes", activity: "Lunch" },
    { period: "3",  time: "3:45-4:15pm",  duration: "30 minutes", activity: "Recess" },
    { period: "4",  time: "4:20-5:15",    duration: "55 minutes", activity: "Quran" },
    { period: "5",  time: "5:30-5:45",    duration: "15 minutes", activity: "Asr" },
    { period: "",   time: "5:45 PM",      duration: "Dismissal",  activity: "" },
  ],
};

const SCHEDULE_STANDARD = {
  AM: [
    { period: "0",  time: "10:00-10:15 AM", duration: "15 minutes", activity: "Arrival" },
    { period: "0+", time: "10:15-10:30",    duration: "15 minutes", activity: "Assembly" },
    { period: "1",  time: "10:30-11:25 AM", duration: "55 minutes", activity: "Islamic Studies" },
    { period: "2",  time: "11:25-11:45 PM", duration: "20 minutes", activity: "Lunch" },
    { period: "3",  time: "11:45-12:30 PM", duration: "45 minutes", activity: "Recess" },
    { period: "",   time: "12:30-12:45",    duration: "15 minutes", activity: "Duhr" },
    { period: "4",  time: "12:45-1:40PM",   duration: "55 minutes", activity: "Quran" },
    { period: "5",  time: "1:40-1:45",      duration: "5 minutes",  activity: "Transition" },
    { period: "",   time: "1:45 PM",        duration: "Dismissal",  activity: "" },
  ],
  PM: [
    { period: "0",  time: "2:00-2:15pm",  duration: "15 minutes", activity: "Staff Meeting" },
    { period: "0+", time: "2:15-2:30pm",  duration: "15 minutes", activity: "Assembly" },
    { period: "1",  time: "2:30-3:25 pm", duration: "55 minutes", activity: "Islamic Studies" },
    { period: "2",  time: "3:25-3:45 pm", duration: "20 minutes", activity: "Lunch" },
    { period: "",   time: "3:45-4:00",    duration: "15 minutes", activity: "Asr" },
    { period: "3",  time: "4:00-4:30pm",  duration: "30 minutes", activity: "Recess" },
    { period: "4",  time: "4:30-5:25",    duration: "55 minutes", activity: "Quran" },
    { period: "5",  time: "5:30-5:45",    duration: "15 minutes", activity: "Maghrib" },
    { period: "",   time: "5:45 PM",      duration: "Dismissal",  activity: "" },
  ],
};

// Seattle is US Pacific: daylight saving runs from the second Sunday in
// March to the first Sunday in November. Compared at whole-day resolution
// because a schedule only ever changes between Sundays, never mid-lesson.
function nthSundayOfMonth(year, monthIndex, n) {
  const first = new Date(year, monthIndex, 1);
  const offsetToFirstSunday = (7 - first.getDay()) % 7;
  return new Date(year, monthIndex, 1 + offsetToFirstSunday + (n - 1) * 7);
}

function daylightSavingRange(year) {
  return { start: nthSundayOfMonth(year, 2, 2), end: nthSundayOfMonth(year, 10, 1) };
}

function isDaylightSaving(date = new Date()) {
  const { start, end } = daylightSavingRange(date.getFullYear());
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return day >= start && day < end;
}

// The stretch of the year each schedule covers, so the toggle can name the
// months rather than leaving teachers to work out when the schedule flips.
// Standard time straddles New Year, so its range is built from whichever
// pair of daylight saving periods currently brackets it.
function scheduleSeasonRanges(date = new Date()) {
  const year = date.getFullYear();
  const current = daylightSavingRange(year);
  const day = new Date(year, date.getMonth(), date.getDate());
  const standard =
    day < current.start
      ? { from: daylightSavingRange(year - 1).end, to: current.start }
      : { from: current.end, to: daylightSavingRange(year + 1).start };
  return { dst: { from: current.start, to: current.end }, standard };
}

// "March 8 to November 1, 2026" within one year, "November 1, 2026 to
// March 14, 2027" across two, so the year is stated only where it matters.
function formatScheduleRange({ from, to }) {
  const sameYear = from.getFullYear() === to.getFullYear();
  const opts = { month: "long", day: "numeric" };
  const start = sameYear
    ? from.toLocaleDateString(undefined, opts)
    : from.toLocaleDateString(undefined, { ...opts, year: "numeric" });
  const end = to.toLocaleDateString(undefined, { ...opts, year: "numeric" });
  return `${start} to ${end}`;
}

/**
 * Daylight saving / standard time switch shared by the Schedule and
 * Resources screens. Defaults to whichever is right for today so nobody
 * has to remember to flip it, but stays switchable for looking ahead.
 */
function ScheduleTimeToggle({ mode, setMode }) {
  const auto = isDaylightSaving() ? "dst" : "standard";
  const ranges = scheduleSeasonRanges();
  const options = [
    { key: "dst", label: "Daylight Saving", range: ranges.dst },
    { key: "standard", label: "Standard Time", range: ranges.standard },
  ];

  return (
    <div className="mb-3">
      <div className="flex gap-1 flex-wrap">
        {options.map((opt) => (
          <button
            key={opt.key}
            onClick={() => setMode(opt.key)}
            className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
              mode === opt.key
                ? "bg-[#7c1d1d] text-white"
                : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
            }`}
          >
            {opt.label}
            {opt.key === auto && <span className="ml-1 opacity-70">(now)</span>}
          </button>
        ))}
      </div>
      <div className="mt-1 space-y-0.5">
        {options.map((opt) => (
          <p
            key={opt.key}
            className={`text-[10px] ${
              mode === opt.key
                ? "text-gray-700 dark:text-gray-300 font-medium"
                : "text-gray-500 dark:text-gray-400"
            }`}
          >
            {opt.label}: {formatScheduleRange(opt.range)}
          </p>
        ))}
        {mode !== auto && (
          <p className="text-[10px] text-orange-700 dark:text-orange-400">
            You are viewing the schedule for the other half of the year.
          </p>
        )}
      </div>
    </div>
  );
}

function Schedule() {
  const [session, setSession] = useState("AM");
  const [timeMode, setTimeMode] = useState(() => (isDaylightSaving() ? "dst" : "standard"));

  const schedule = (timeMode === "dst" ? SCHEDULE_DST : SCHEDULE_STANDARD)[session];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 md:p-4 border-t-4 border-[#7c1d1d] dark:border-red-600">
      <h2 className="text-base font-semibold mb-3 text-[#7c1d1d] dark:text-red-400">Schedule</h2>

      <ScheduleTimeToggle mode={timeMode} setMode={setTimeMode} />

      <div className="mb-2">
        <label className="block text-[10px] font-medium mb-1 dark:text-gray-300">Session</label>
        <select
          value={session}
          onChange={(e) => setSession(e.target.value)}
          className="border dark:border-gray-600 rounded p-1 w-48 text-xs dark:bg-gray-700 dark:text-white"
        >
          <option value="AM">AM Schedule</option>
          <option value="PM">PM Schedule</option>
        </select>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-[#7c1d1d] dark:bg-red-900 text-white">
              <th className="p-1 border border-white dark:border-gray-600 text-left font-semibold">Period</th>
              <th className="p-1 border border-white dark:border-gray-600 text-left font-semibold">Time</th>
              <th className="p-1 border border-white dark:border-gray-600 text-left font-semibold">Duration</th>
              <th className="p-1 border border-white dark:border-gray-600 text-left font-semibold">{session === "AM" ? "" : "Staff"}</th>
            </tr>
          </thead>
          <tbody>
            {schedule.map((item, index) => (
              <tr key={index} className={index % 2 === 0 ? "bg-gray-50 dark:bg-gray-700" : "bg-white dark:bg-gray-800"}>
                <td className="p-1 border border-gray-300 dark:border-gray-600 dark:text-gray-200">{item.period}</td>
                <td className="p-1 border border-gray-300 dark:border-gray-600 dark:text-gray-200">{item.time}</td>
                <td className="p-1 border border-gray-300 dark:border-gray-600 dark:text-gray-200">{item.duration}</td>
                <td className="p-1 border border-gray-300 dark:border-gray-600 font-medium dark:text-gray-200">{item.activity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* =========================================================
   RESOURCES (Schedule + Access Codes)
   ========================================================= */

function Resources({ state, setState, user }) {
  const [session, setSession] = useState("AM");
  const [timeMode, setTimeMode] = useState(() => (isDaylightSaving() ? "dst" : "standard"));

  const schedule = (timeMode === "dst" ? SCHEDULE_DST : SCHEDULE_STANDARD)[session];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 md:p-4 border-t-4 border-[#7c1d1d] dark:border-red-600">
      <h2 className="text-base font-semibold mb-3 text-[#7c1d1d] dark:text-red-400">📚 Resources</h2>

      {/* Schedule Section */}
      <div className="mb-6">
        <h3 className="text-base font-semibold mb-3 text-[#7c1d1d] dark:text-red-400">📅 Daily Schedule</h3>

        <ScheduleTimeToggle mode={timeMode} setMode={setTimeMode} />

        <div className="mb-3">
          <label className="block text-sm font-medium mb-1 dark:text-gray-300">Session</label>
          <select
            value={session}
            onChange={(e) => setSession(e.target.value)}
            className="border dark:border-gray-600 rounded p-2 w-48 text-sm dark:bg-gray-700 dark:text-white"
          >
            <option value="AM">AM Schedule</option>
            <option value="PM">PM Schedule</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#7c1d1d] dark:bg-red-900 text-white">
                <th className="p-2 border border-white dark:border-gray-600 text-left font-semibold">Period</th>
                <th className="p-2 border border-white dark:border-gray-600 text-left font-semibold">Time</th>
                <th className="p-2 border border-white dark:border-gray-600 text-left font-semibold">Duration</th>
                <th className="p-2 border border-white dark:border-gray-600 text-left font-semibold">{session === "AM" ? "" : "Staff"}</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map((item, index) => (
                <tr key={index} className={index % 2 === 0 ? "bg-gray-50 dark:bg-gray-700" : "bg-white dark:bg-gray-800"}>
                  <td className="p-2 border border-gray-300 dark:border-gray-600 dark:text-gray-200">{item.period}</td>
                  <td className="p-2 border border-gray-300 dark:border-gray-600 dark:text-gray-200">{item.time}</td>
                  <td className="p-2 border border-gray-300 dark:border-gray-600 dark:text-gray-200">{item.duration}</td>
                  <td className="p-2 border border-gray-300 dark:border-gray-600 font-medium dark:text-gray-200">{item.activity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Access Codes Section */}
      <div className="pt-6 border-t-2 border-gray-200 dark:border-gray-600">
        <AccessCodesSection state={state} setState={setState} user={user} />
      </div>
    </div>
  );
}

/* =========================================================
   LUNCH ORDERS
   ========================================================= */

function LunchOrders({ state, setState, user }) {
  const [selectedGrade, setSelectedGrade] = useState("");
  const [orders, setOrders] = useState({});
  const [sending, setSending] = useState(false);
  const [viewMode, setViewMode] = useState("submit"); // "submit" or "view"

  // Get available grades based on user role
  const availableGrades = user.role === "admin"
    ? [...GRADES_AM, ...GRADES_PM]
    : user.gradeLevel
    ? [user.gradeLevel]
    : [];

  // Get students for selected grade
  const students = selectedGrade ? (state.studentsByGrade[selectedGrade] || []) : [];

  const handleOrderChange = (studentId, orderType) => {
    setOrders(prev => ({
      ...prev,
      [studentId]: orderType
    }));
  };

  const sendLunchOrders = async () => {
    if (!selectedGrade || Object.keys(orders).length === 0) {
      alert("Please select a grade and add at least one order.");
      return;
    }

    setSending(true);

    try {
      // Prepare order data
      const orderItems = students
        .filter(student => orders[student.id])
        .map(student => ({
          studentName: student.name,
          order: orders[student.id]
        }));

      const orderList = orderItems
        .map(item => `<tr><td style="padding: 8px; border: 1px solid #ddd;">${item.studentName}</td><td style="padding: 8px; border: 1px solid #ddd; font-weight: bold;">${item.order}</td></tr>`)
        .join('');

      // Send email via Brevo API
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: 'awadallahyasseen@gmail.com',
          toName: 'Sunday School Lunch Orders',
          subject: `Lunch Orders - ${selectedGrade} - ${new Date().toLocaleDateString()}`,
          htmlContent: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #7c1d1d;">MAPS Sunday School - Lunch Orders</h2>
              <p><strong>Grade:</strong> ${selectedGrade}</p>
              <p><strong>Submitted by:</strong> ${user.name} (${user.email})</p>
              <p><strong>Date:</strong> ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
              <h3 style="margin-top: 20px;">Orders:</h3>
              <table style="width: 100%; border-collapse: collapse; margin-top: 10px;">
                <thead>
                  <tr style="background-color: #7c1d1d; color: white;">
                    <th style="padding: 10px; border: 1px solid #ddd; text-align: left;">Student Name</th>
                    <th style="padding: 10px; border: 1px solid #ddd; text-align: left;">Order</th>
                  </tr>
                </thead>
                <tbody>
                  ${orderList}
                </tbody>
              </table>
              <hr style="margin: 20px 0; border: none; border-top: 1px solid #ccc;">
              <p style="color: #666; font-size: 12px;">
                MAPS Sunday School Logistics Management System
              </p>
            </div>
          `
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to send email: ${response.status} - ${JSON.stringify(errorData)}`);
      }

      console.log("✅ Lunch orders email sent successfully");

      // Save to state
      const newOrder = {
        id: uid(),
        grade: selectedGrade,
        submittedBy: user.name,
        submittedByEmail: user.email,
        date: new Date().toISOString(),
        items: orderItems
      };

      setState(s => ({
        ...s,
        lunchOrders: [...(s.lunchOrders || []), newOrder]
      }));

      alert("Lunch orders sent successfully!");
      setOrders({});
    } catch (error) {
      console.error("Error sending lunch orders:", error);
      alert("Failed to send lunch orders. Please try again or contact awadallahyasseen@gmail.com directly.");
    } finally {
      setSending(false);
    }
  };

  // Group lunch orders by grade for admin view
  const ordersByGrade = (state.lunchOrders || []).reduce((acc, order) => {
    if (!acc[order.grade]) {
      acc[order.grade] = [];
    }
    acc[order.grade].push(order);
    return acc;
  }, {});

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 md:p-4 border-t-4 border-[#7c1d1d] dark:border-red-600">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-sm font-bold text-[#7c1d1d] dark:text-red-400">Lunch Orders</h2>

          {user.role === "admin" && (
            <div className="flex gap-1">
              <button
                onClick={() => setViewMode("submit")}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                  viewMode === "submit"
                    ? "bg-[#7c1d1d] text-white"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                }`}
              >
                Submit Orders
              </button>
              <button
                onClick={() => setViewMode("view")}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                  viewMode === "view"
                    ? "bg-[#7c1d1d] text-white"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                }`}
              >
                View All Orders
              </button>
            </div>
          )}
        </div>

        {viewMode === "submit" ? (
          <>
            {/* Grade Selection */}
            <div className="mb-2">
              <label className="block text-[10px] font-medium text-gray-700 dark:text-gray-300 mb-1">
                Select Grade
              </label>
              <select
                value={selectedGrade}
                onChange={(e) => {
                  setSelectedGrade(e.target.value);
                  setOrders({});
                }}
                className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded p-1 text-xs focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent"
              >
                <option value="">-- Select Grade --</option>
                {availableGrades.map(grade => (
                  <option key={grade} value={grade}>{grade}</option>
                ))}
              </select>
            </div>

            {/* Student Orders */}
            {selectedGrade && students.length > 0 && (
              <>
                <div className="mb-2">
                  <h3 className="text-xs font-semibold text-gray-800 dark:text-gray-200 mb-1">
                    Students in {selectedGrade}
                  </h3>
                  <div className="space-y-1">
                    {students.map(student => (
                      <div key={student.id} className="flex items-center gap-2 p-1 bg-gray-50 dark:bg-gray-700 rounded">
                        <span className="flex-1 text-gray-800 dark:text-gray-200 font-medium text-xs">
                          {student.name}
                        </span>
                        <select
                          value={orders[student.id] || ""}
                          onChange={(e) => handleOrderChange(student.id, e.target.value)}
                          className="border border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded p-1 text-xs focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent"
                        >
                          <option value="">-- No Order --</option>
                          <option value="Pizza">Pizza</option>
                          <option value="Sandwich">Sandwich</option>
                          <option value="Salad">Salad</option>
                          <option value="Pasta">Pasta</option>
                          <option value="Burger">Burger</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  onClick={sendLunchOrders}
                  disabled={sending || Object.keys(orders).length === 0}
                  className="w-full bg-[#7c1d1d] dark:bg-red-700 text-white px-2 py-1.5 rounded text-xs font-semibold hover:bg-red-800 dark:hover:bg-red-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {sending ? "Sending..." : `Send ${Object.keys(orders).length} Order(s)`}
                </button>
              </>
            )}

            {selectedGrade && students.length === 0 && (
              <p className="text-gray-600 dark:text-gray-400 text-center py-2 text-xs">
                No students found in {selectedGrade}
              </p>
            )}
          </>
        ) : (
          /* Admin View - All Orders by Grade */
          <div className="space-y-2">
            {Object.keys(ordersByGrade).length === 0 ? (
              <p className="text-gray-600 dark:text-gray-400 text-center py-4 text-xs">
                No lunch orders submitted yet
              </p>
            ) : (
              Object.keys(ordersByGrade).sort().map(grade => (
                <div key={grade} className="border dark:border-gray-600 rounded p-2">
                  <h3 className="text-xs font-bold text-[#7c1d1d] dark:text-red-400 mb-1">
                    {grade}
                  </h3>
                  <div className="space-y-1">
                    {ordersByGrade[grade].map(order => (
                      <div key={order.id} className="bg-gray-50 dark:bg-gray-700 rounded p-2">
                        <div className="flex justify-between items-start mb-1">
                          <div>
                            <p className="text-[10px] text-gray-600 dark:text-gray-400">
                              Submitted by: <span className="font-semibold text-gray-800 dark:text-gray-200">{order.submittedBy}</span>
                            </p>
                            <p className="text-[10px] text-gray-600 dark:text-gray-400">
                              Date: {new Date(order.date).toLocaleDateString()} at {new Date(order.date).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                          {order.items.map((item, idx) => (
                            <div key={idx} className="flex justify-between items-center p-1 bg-white dark:bg-gray-600 rounded">
                              <span className="font-medium text-gray-800 dark:text-gray-200 text-xs">{item.studentName}</span>
                              <span className="text-[#7c1d1d] dark:text-red-400 font-semibold text-xs">{item.order}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
    </div>
  );
}

/* =========================================================
   ACCESS CODES
   ========================================================= */

function AccessCodes({ state, setState, user }) {
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editCode, setEditCode] = useState("");
  const [newName, setNewName] = useState("");
  const [newCode, setNewCode] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);

  const codes = state.accessCodes || [];
  const isAdmin = user.role === "admin";

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditName(item.name);
    setEditCode(item.code);
  };

  const saveEdit = (id) => {
    if (!editName.trim() || !editCode.trim()) {
      alert("Please fill in both name and code");
      return;
    }

    setState((s) => ({
      ...s,
      accessCodes: s.accessCodes.map((item) =>
        item.id === id ? { ...item, name: editName.trim(), code: editCode.trim() } : item
      ),
    }));

    setEditingId(null);
    setEditName("");
    setEditCode("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditCode("");
  };

  const addCode = () => {
    if (!newName.trim() || !newCode.trim()) {
      alert("Please fill in both name and code");
      return;
    }

    const newAccessCode = {
      id: uid(),
      name: newName.trim(),
      code: newCode.trim(),
    };

    setState((s) => ({
      ...s,
      accessCodes: [...s.accessCodes, newAccessCode],
    }));

    setNewName("");
    setNewCode("");
    setShowAddForm(false);
  };

  const deleteCode = (id) => {
    if (window.confirm("Are you sure you want to delete this access code?")) {
      setState((s) => ({
        ...s,
        accessCodes: s.accessCodes.filter((item) => item.id !== id),
      }));
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 md:p-4 border-t-4 border-[#7c1d1d] dark:border-red-600">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-sm font-semibold text-[#7c1d1d] dark:text-red-400">Access Codes</h2>
        {isAdmin && (
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-[#7c1d1d] text-white px-2 py-1 rounded hover:bg-[#651818] font-medium transition-colors text-xs"
          >
            {showAddForm ? "Cancel" : "+ Add New Code"}
          </button>
        )}
      </div>

      {/* Add New Code Form */}
      {isAdmin && showAddForm && (
        <div className="mb-2 p-2 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
          <h3 className="font-semibold text-xs text-gray-800 dark:text-gray-200 mb-1">Add New Access Code</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
            <input
              type="text"
              placeholder="Name (e.g., Copier)"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded p-1 text-xs focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-600 dark:text-white"
            />
            <input
              type="text"
              placeholder="Code (e.g., 5312)"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded p-1 text-xs focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-600 dark:text-white"
            />
          </div>
          <button
            onClick={addCode}
            className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700 font-medium transition-colors"
          >
            Add Code
          </button>
        </div>
      )}

      {/* Access Codes List */}
      <div className="space-y-1">
        {codes.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-center py-2 text-xs">No access codes yet.</p>
        ) : (
          codes.map((item) => {
            const isEditing = editingId === item.id;

            return (
              <div
                key={item.id}
                className="flex items-center justify-between py-1 border-b border-gray-200 dark:border-gray-600 last:border-0"
              >
                {isEditing ? (
                  // Edit Mode
                  <div className="flex-1 flex items-center gap-1">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="flex-1 border border-gray-300 dark:border-gray-600 rounded p-1 text-xs focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-600 dark:text-white"
                      placeholder="Name"
                    />
                    <input
                      type="text"
                      value={editCode}
                      onChange={(e) => setEditCode(e.target.value)}
                      className="flex-1 border border-gray-300 dark:border-gray-600 rounded p-1 text-xs focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-600 dark:text-white font-mono"
                      placeholder="Code"
                    />
                    <button
                      onClick={() => saveEdit(item.id)}
                      className="bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 text-xs"
                    >
                      Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="bg-gray-500 text-white px-2 py-1 rounded hover:bg-gray-600 text-xs"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  // View Mode
                  <>
                    <span className="text-xs text-gray-600 dark:text-gray-300">{item.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono font-semibold text-[#7c1d1d] dark:text-red-400">
                        {item.code}
                      </span>
                      {isAdmin && (
                        <div className="flex gap-1">
                          <button
                            onClick={() => startEdit(item)}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-xs font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteCode(item.id)}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-xs font-medium"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/* =========================================================
   ANNOUNCEMENTS (Admin Only)
   ========================================================= */

function Announcements({ state, setState, user }) {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [adminTargetGrades, setAdminTargetGrades] = useState([]); // For admin grade selection (multiple)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // For dropdown toggle
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editMessage, setEditMessage] = useState("");

  // Function to fetch announcements from Supabase
  const fetchAnnouncements = async () => {
    console.log("🔄 Auto-checking for new announcements from Supabase...");
    try {
      const { data, error } = await supabase
        .from('announcements')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      console.log("📢 Loaded announcements from Supabase:", data.length);
      setState((s) => ({
        ...s,
        announcements: data,
      }));
    } catch (error) {
      console.error("❌ Error fetching announcements:", error);
    }
  };

  // Load and auto-refresh announcements from Supabase every 5 seconds
  useEffect(() => {
    console.log("🔄 Announcements: Auto-refresh ENABLED - checking Supabase every 5 seconds");

    // Fetch immediately on mount
    fetchAnnouncements();

    // Then fetch every 5 seconds
    const interval = setInterval(fetchAnnouncements, 5000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const postAnnouncement = async () => {
    if (!title.trim() || !message.trim()) {
      alert("Please fill in both title and message");
      return;
    }

    try {
      // For admin with "Everyone" selected, create a single announcement for all users
      if (user.role === "admin" && adminTargetGrades.includes("Everyone")) {
        const announcement = {
          title: title.trim(),
          message: message.trim(),
          author: user.name,
          target_grade: "Everyone",
          posted_by: "admin",
        };

        const { error } = await supabase
          .from('announcements')
          .insert([announcement]);

        if (error) throw error;
      }
      // For admin with multiple grades selected, create separate announcements for each grade
      else if (user.role === "admin" && adminTargetGrades.length > 0) {
        const newAnnouncements = adminTargetGrades.map(grade => ({
          title: title.trim(),
          message: message.trim(),
          author: user.name,
          target_grade: grade,
          posted_by: "admin",
        }));

        const { error } = await supabase
          .from('announcements')
          .insert(newAnnouncements);

        if (error) throw error;
      } else {
        // Single announcement (admin with no grades = global, or team lead)
        const announcement = {
          title: title.trim(),
          message: message.trim(),
          author: user.name,
          target_grade: user.role === "admin" ? null : user.teamLeadGrade,
          posted_by: user.role === "admin" ? "admin" : "teamlead",
        };

        const { error } = await supabase
          .from('announcements')
          .insert([announcement]);

        if (error) throw error;
      }

      console.log("✅ Announcement posted to Supabase");

      // Immediately fetch announcements to show the new one
      await fetchAnnouncements();

      setTitle("");
      setMessage("");
      setAdminTargetGrades([]);
      alert("Announcement posted successfully!");
    } catch (error) {
      console.error("❌ Error posting announcement:", error);
      alert("Failed to post announcement: " + error.message);
    }
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;

    try {
      // Delete from Supabase
      const { error } = await supabase
        .from('announcements')
        .delete()
        .eq('id', id);

      if (error) throw error;

      console.log("✅ Announcement deleted from Supabase");

      // Immediately fetch announcements to update the list
      await fetchAnnouncements();
    } catch (error) {
      console.error("❌ Error deleting announcement:", error);
      alert("Failed to delete announcement: " + error.message);
    }
  };

  const startEdit = (announcement) => {
    setEditingId(announcement.id);
    setEditTitle(announcement.title);
    setEditMessage(announcement.message);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle("");
    setEditMessage("");
  };

  const saveEdit = (id) => {
    if (!editTitle.trim() || !editMessage.trim()) {
      alert("Please fill in both title and message");
      return;
    }

    setState((s) => ({
      ...s,
      announcements: s.announcements.map((a) =>
        a.id === id
          ? { ...a, title: editTitle.trim(), message: editMessage.trim() }
          : a
      ),
    }));

    setEditingId(null);
    setEditTitle("");
    setEditMessage("");
    alert("Announcement updated successfully!");
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 md:p-4 border-t-4 border-[#7c1d1d] dark:border-red-600">
      <h2 className="text-base font-semibold mb-3 text-[#7c1d1d] dark:text-red-400">
        Announcements
      </h2>

      {(user.role === "admin" || user.isTeamLead) && (
        <div className="mb-2 p-2 border border-[#7c1d1d] dark:border-red-600 rounded bg-red-50 dark:bg-gray-700">
          <h3 className="font-semibold mb-2 text-xs text-[#7c1d1d] dark:text-red-400">
            {user.role === "admin" ? "Post Announcement" : "Post Grade-Specific Announcement"}
          </h3>
          <input
            type="text"
            placeholder="Announcement Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full border border-gray-300 dark:border-gray-600 rounded p-1 mb-2 text-xs focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-600 dark:text-white"
          />
          <textarea
            placeholder="Announcement Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            className="w-full border border-gray-300 dark:border-gray-600 rounded p-1 mb-2 text-xs focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-600 dark:text-white"
          />
          {user.role === "admin" && (
            <div className="mb-2 relative">
              <label className="block text-[10px] font-medium text-gray-700 dark:text-gray-300 mb-1">
                Target Audience (Select Multiple)
              </label>

              {/* Dropdown Button */}
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded p-1 text-xs focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-600 dark:text-white text-left flex justify-between items-center"
              >
                <span className="text-xs">
                  {adminTargetGrades.includes("Everyone")
                    ? "Everyone"
                    : adminTargetGrades.length > 0
                    ? `${adminTargetGrades.length} grade(s) selected: ${adminTargetGrades.join(", ")}`
                    : "Select grades (or leave empty for all teachers)"
                  }
                </span>
                <svg className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Content */}
              {isDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 shadow-lg max-h-80 overflow-y-auto">
                  <div className="p-3">
                    {/* Everyone Option */}
                    <div className="mb-3 pb-3 border-b border-gray-300 dark:border-gray-600">
                      <label className="flex items-center space-x-2 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={adminTargetGrades.includes("Everyone")}
                          onChange={(e) => {
                            if (e.target.checked) {
                              // If "Everyone" is selected, clear all other grades
                              setAdminTargetGrades(["Everyone"]);
                            } else {
                              setAdminTargetGrades([]);
                            }
                          }}
                          className="w-4 h-4 text-blue-600 focus:ring-blue-600 rounded"
                        />
                        <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">Everyone (All Users)</span>
                      </label>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 ml-6">
                        Send to all users including admins, teachers, and team leads
                      </p>
                    </div>

                    {/* Team Lead Grades */}
                    <div className="mb-3">
                      <p className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Team Lead Grades</p>
                      <div className="grid grid-cols-2 gap-2">
                        {["PreK", "Kindergarten", "1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th", "9th", "10th"].map(grade => (
                          <label key={grade} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 p-1 rounded">
                            <input
                              type="checkbox"
                              checked={adminTargetGrades.includes(grade) && !adminTargetGrades.includes("Everyone")}
                              disabled={adminTargetGrades.includes("Everyone")}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setAdminTargetGrades([...adminTargetGrades, grade]);
                                } else {
                                  setAdminTargetGrades(adminTargetGrades.filter(g => g !== grade));
                                }
                              }}
                              className="w-4 h-4 text-[#7c1d1d] focus:ring-[#7c1d1d] rounded disabled:opacity-50"
                            />
                            <span className={`text-sm ${adminTargetGrades.includes("Everyone") ? "text-gray-400" : "text-gray-700 dark:text-gray-300"}`}>{grade}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Specific Classes */}
                    <div>
                      <p className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Specific Classes</p>
                      <div className="grid grid-cols-2 gap-2">
                        {["Pre-K", "KG A", "KG B", "1A", "1B", "2A", "2B", "3A", "3B", "4A", "4B", "5A", "5B", "6A", "6B", "7", "8", "9", "10"].map(grade => (
                          <label key={grade} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 p-1 rounded">
                            <input
                              type="checkbox"
                              checked={adminTargetGrades.includes(grade) && !adminTargetGrades.includes("Everyone")}
                              disabled={adminTargetGrades.includes("Everyone")}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setAdminTargetGrades([...adminTargetGrades, grade]);
                                } else {
                                  setAdminTargetGrades(adminTargetGrades.filter(g => g !== grade));
                                }
                              }}
                              className="w-4 h-4 text-[#7c1d1d] focus:ring-[#7c1d1d] rounded disabled:opacity-50"
                            />
                            <span className={`text-sm ${adminTargetGrades.includes("Everyone") ? "text-gray-400" : "text-gray-700 dark:text-gray-300"}`}>{grade}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                {adminTargetGrades.includes("Everyone")
                  ? "Sending to EVERYONE (all users)"
                  : adminTargetGrades.length > 0
                  ? `Selected ${adminTargetGrades.length} grade(s): ${adminTargetGrades.join(", ")}`
                  : "No grades selected - this will be a global announcement to all teachers"
                }
              </p>
            </div>
          )}
          {user.isTeamLead && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Announcing to: <span className="font-bold text-[#7c1d1d] dark:text-red-400">{user.teamLeadGrade}</span>
              </label>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                As a team lead, you can only post announcements to your assigned grade level.
              </p>
            </div>
          )}
          <button
            onClick={postAnnouncement}
            className="bg-[#7c1d1d] text-white px-4 py-2 rounded-lg hover:bg-[#651818] font-medium transition-colors"
          >
            Post Announcement
          </button>
        </div>
      )}

      <div className="space-y-4">
        {(() => {
          // Helper function to check if user's grade matches the announcement's target grade
          const gradeMatches = (targetGrade, userGrade) => {
            // Exact match (6A to 6A, 7 to 7, etc.)
            if (targetGrade === userGrade) return true;

            // Normalize grades: "Grade 6A" → "6A", "Grade 1 AM" → "1A" (for AM/PM)
            const normalizeGrade = (grade) => {
              if (!grade) return grade;
              // Remove "Grade " prefix: "Grade 6A" → "6A"
              let normalized = grade.replace(/^Grade\s+/i, '');
              // Convert AM/PM format: "1 AM" → "1A", "2 PM" → "2B"
              normalized = normalized.replace(/(\d+)\s*AM$/i, '$1A');
              normalized = normalized.replace(/(\d+)\s*PM$/i, '$1B');
              return normalized;
            };

            const normalizedTarget = normalizeGrade(targetGrade);
            const normalizedUser = normalizeGrade(userGrade);

            // Check normalized match: "6A" to "6A"
            if (normalizedTarget === normalizedUser) return true;

            // Map team lead grades to actual grade levels (A/B classes for 1-6, single classes for 7-10)
            const gradeMapping = {
              "PreK": ["Pre-K"],
              "Kindergarten": ["KG A", "KG B"],
              "1st": ["1A", "1B"],
              "2nd": ["2A", "2B"],
              "3rd": ["3A", "3B"],
              "4th": ["4A", "4B"],
              "5th": ["5A", "5B"],
              "6th": ["6A", "6B"],
              "7th": ["7"],
              "8th": ["8"],
              "9th": ["9"],
              "10th": ["10"]
            };

            // If target grade is a group (like "6th"), show to all users in that group
            // Example: "6th" announcement shows to "6A", "6B"
            const mappedGrades = gradeMapping[targetGrade];
            if (mappedGrades && mappedGrades.includes(normalizedUser)) return true;

            return false;
          };

          // Filter announcements based on user's grade level
          console.log('📊 Filtering announcements. Total:', (state.announcements || []).length);
          console.log('👤 Current user role:', user.role);
          const filteredAnnouncements = (state.announcements || []).filter((announcement) => {
            // Admins can see ALL announcements
            if (user.role === "admin") return true;

            // Get target grade (handle both old targetGrade and new target_grade from Supabase)
            const targetGrade = announcement.target_grade || announcement.targetGrade;

            // Show announcements marked as "Everyone" to all users
            if (targetGrade === "Everyone") return true;

            // Show all global announcements (admin posts with no targetGrade)
            if (!targetGrade) return true;

            // If user is a team lead, show announcements for their team lead grade
            if (user.isTeamLead && targetGrade === user.teamLeadGrade) {
              return true;
            }

            // Show grade-specific announcements if they match user's grade
            return gradeMatches(targetGrade, user.gradeLevel);
          });

          if (filteredAnnouncements.length === 0) {
            return (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No announcements yet
              </div>
            );
          }

          return filteredAnnouncements.map((announcement) => {
            const canEdit = user.role === "admin" || (user.isTeamLead && announcement.author === user.name);
            const isEditing = editingId === announcement.id;

            return (
              <div
                key={announcement.id}
                className="border dark:border-gray-600 rounded-lg p-4 bg-gradient-to-r from-red-50 to-white dark:from-gray-700 dark:to-gray-800"
              >
                {isEditing ? (
                  // Edit Mode
                  <div>
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 mb-3 focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-600 dark:text-white font-bold"
                      placeholder="Announcement Title"
                    />
                    <textarea
                      value={editMessage}
                      onChange={(e) => setEditMessage(e.target.value)}
                      rows={4}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 mb-3 focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-600 dark:text-white"
                      placeholder="Announcement Message"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => saveEdit(announcement.id)}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 font-medium transition-colors"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 font-medium transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-[#7c1d1d] dark:text-red-400">
                          {announcement.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {(announcement.target_grade || announcement.targetGrade) && (
                            <span className={`inline-block text-xs text-white px-2 py-1 rounded ${
                              (announcement.target_grade || announcement.targetGrade) === "Everyone"
                                ? "bg-blue-600 dark:bg-blue-500 font-semibold"
                                : "bg-[#7c1d1d] dark:bg-red-600"
                            }`}>
                              {(announcement.target_grade || announcement.targetGrade) === "Everyone" ? "📢 Everyone" : (announcement.target_grade || announcement.targetGrade)}
                            </span>
                          )}
                          {user.role === "admin" && (
                            <span className="inline-block text-xs bg-gray-600 dark:bg-gray-500 text-white px-2 py-1 rounded">
                              Posted by: {announcement.author}
                            </span>
                          )}
                        </div>
                      </div>
                      {canEdit && (
                        <div className="flex gap-2 ml-2">
                          <button
                            onClick={() => startEdit(announcement)}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteAnnouncement(announcement.id)}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 mb-2 whitespace-pre-wrap">{announcement.message}</p>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {user.role === "admin" ? (
                        <>Posted on {new Date(announcement.created_at || announcement.date).toLocaleString()}</>
                      ) : (
                        <>Posted by {announcement.author} on {new Date(announcement.created_at || announcement.date).toLocaleString()}</>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          });
        })()}
      </div>
    </div>
  );
}

/* =========================================================
   PRINT PAGE
   ========================================================= */

function PrintPage() {
  const [showPopup, setShowPopup] = useState(false);
  const [documentName, setDocumentName] = useState("");
  const [notes, setNotes] = useState("");
  const [attachedFiles, setAttachedFiles] = useState([]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachedFiles(files);
  };

  const sendToPrint = async () => {
    if (!documentName.trim()) {
      alert("Please enter a document name");
      return;
    }

    try {
      // Convert files to base64 attachments
      const attachments = [];
      if (attachedFiles.length > 0) {
        const filePromises = attachedFiles.map((file) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                name: file.name,
                content: e.target.result.split(',')[1], // Get base64 data without the data URL prefix
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        });

        const filesData = await Promise.all(filePromises);
        attachments.push(...filesData);
      }

      // Send email via Brevo API
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: 'sundayschoolprinting@gmail.com',
          toName: 'Sunday School Printing',
          subject: `Print Request: ${documentName.trim()}`,
          htmlContent: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #7c1d1d;">MAPS Sunday School - Print Request</h2>
              <p><strong>Document Name:</strong> ${documentName.trim()}</p>
              <p><strong>Notes:</strong> ${notes.trim() || 'None'}</p>
              <p><strong>Submitted:</strong> ${new Date().toLocaleString()}</p>
              ${attachments.length > 0
                ? `<p><strong>Attached Files:</strong> ${attachments.map(f => f.name).join(', ')}</p>`
                : '<p><strong>Attached Files:</strong> None</p>'
              }
              <hr style="margin: 20px 0; border: none; border-top: 1px solid #ccc;">
              <p style="color: #666; font-size: 12px;">
                MAPS Sunday School Logistics Management System
              </p>
            </div>
          `,
          attachments: attachments
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to send email: ${response.status} - ${JSON.stringify(errorData)}`);
      }

      console.log("✅ Print request email sent successfully");
      alert(`Print request sent for: ${documentName}\n\nYour document will be printed and available for pickup.`);
      setShowPopup(false);
      setDocumentName("");
      setNotes("");
      setAttachedFiles([]);
    } catch (error) {
      console.error("❌ Error sending print request:", error);
      alert("Failed to send print request. Please try again or contact sundayschoolprinting@gmail.com directly.");
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 md:p-4 border-t-4 border-[#7c1d1d] dark:border-red-600">
      <h2 className="text-base font-semibold mb-3 text-[#7c1d1d] dark:text-red-400">
        Print Requests
      </h2>

      <div className="text-center py-4">
        <div className="text-4xl mb-2">🖨️</div>
        <h3 className="text-sm font-semibold mb-1 dark:text-gray-200">Send Documents to Print</h3>
        <p className="text-xs text-gray-600 dark:text-gray-300 mb-3">
          Submit your printing requests to sundayschoolprinting@gmail.com
        </p>
        <button
          onClick={() => setShowPopup(true)}
          className="bg-[#7c1d1d] text-white px-3 py-1.5 rounded hover:bg-[#651818] font-medium transition-colors text-xs"
        >
          Send Print Request
        </button>
      </div>

      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-[#7c1d1d] dark:text-red-400 mb-4">
              Print Request
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Document Name *
                </label>
                <input
                  type="text"
                  value={documentName}
                  onChange={(e) => setDocumentName(e.target.value)}
                  placeholder="e.g., Attendance Sheet - Grade 3A"
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Attach Files
                </label>
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#7c1d1d] file:text-white hover:file:bg-[#651818] dark:bg-gray-700 dark:text-white"
                />
                {attachedFiles.length > 0 && (
                  <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                    <strong>Selected files:</strong>
                    <ul className="list-disc list-inside mt-1">
                      {attachedFiles.map((file, index) => (
                        <li key={index}>{file.name} ({(file.size / 1024).toFixed(1)} KB)</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Special instructions, number of copies, etc."
                  rows={3}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                📧 This will send an email to: <b>sundayschoolprinting@gmail.com</b>
              </p>
              <div className="flex gap-3">
                <button
                  onClick={sendToPrint}
                  className="flex-1 bg-[#7c1d1d] text-white px-4 py-2 rounded-lg hover:bg-[#651818] font-medium transition-colors"
                >
                  Send Request
                </button>
                <button
                  onClick={() => {
                    setShowPopup(false);
                    setDocumentName("");
                    setNotes("");
                    setAttachedFiles([]);
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================================
   ACCESS CODES SECTION (Embedded in Resources Page)
   ========================================================= */

function AccessCodesSection({ state, setState, user }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCode, setNewCode] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editCode, setEditCode] = useState("");

  const codes = state.accessCodes || [];
  const isAdmin = user.role === "admin";

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditName(item.name);
    setEditCode(item.code);
  };

  const saveEdit = (id) => {
    if (!editName.trim() || !editCode.trim()) {
      alert("Please fill in both name and code");
      return;
    }

    setState((s) => ({
      ...s,
      accessCodes: s.accessCodes.map((item) =>
        item.id === id ? { ...item, name: editName.trim(), code: editCode.trim() } : item
      ),
    }));

    setEditingId(null);
    setEditName("");
    setEditCode("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditCode("");
  };

  const addCode = () => {
    if (!newName.trim() || !newCode.trim()) {
      alert("Please fill in both name and code");
      return;
    }

    const newAccessCode = {
      id: uid(),
      name: newName.trim(),
      code: newCode.trim(),
    };

    setState((s) => ({
      ...s,
      accessCodes: [...s.accessCodes, newAccessCode],
    }));

    setNewName("");
    setNewCode("");
    setShowAddForm(false);
  };

  const deleteCode = (id) => {
    if (window.confirm("Are you sure you want to delete this access code?")) {
      setState((s) => ({
        ...s,
        accessCodes: s.accessCodes.filter((item) => item.id !== id),
      }));
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border-t-2 border-gray-300 dark:border-gray-600">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-base font-semibold text-[#7c1d1d] dark:text-red-400">📋 Quick Access Codes</h3>
        {isAdmin && (
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-[#7c1d1d] text-white px-3 py-1.5 rounded hover:bg-[#651818] font-medium transition-colors text-xs"
          >
            {showAddForm ? "Cancel" : "+ Add Code"}
          </button>
        )}
      </div>

      {/* Add New Code Form */}
      {isAdmin && showAddForm && (
        <div className="mb-3 p-3 bg-white dark:bg-gray-600 rounded border border-gray-300 dark:border-gray-500">
          <h4 className="font-semibold text-sm text-gray-800 dark:text-gray-200 mb-2">Add New Access Code</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
            <input
              type="text"
              placeholder="Name (e.g., Copier)"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded p-2 text-sm focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
            <input
              type="text"
              placeholder="Code (e.g., 5312)"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded p-2 text-sm focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          <button
            onClick={addCode}
            className="bg-green-600 text-white px-3 py-1.5 rounded text-sm hover:bg-green-700 font-medium transition-colors"
          >
            Add Code
          </button>
        </div>
      )}

      {/* Access Codes List */}
      <div className="space-y-2">
        {codes.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-center py-3 text-sm">No access codes available.</p>
        ) : (
          codes.map((item) => {
            const isEditing = editingId === item.id;

            return (
              <div
                key={item.id}
                className="flex items-center justify-between py-2 px-3 bg-white dark:bg-gray-600 rounded border border-gray-200 dark:border-gray-500"
              >
                {isEditing ? (
                  // Edit Mode
                  <div className="flex-1 flex items-center gap-2">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="flex-1 border border-gray-300 dark:border-gray-600 rounded p-2 text-sm focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Name"
                    />
                    <input
                      type="text"
                      value={editCode}
                      onChange={(e) => setEditCode(e.target.value)}
                      className="flex-1 border border-gray-300 dark:border-gray-600 rounded p-2 text-sm focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent dark:bg-gray-700 dark:text-white font-mono"
                      placeholder="Code"
                    />
                    <button
                      onClick={() => saveEdit(item.id)}
                      className="bg-green-600 text-white px-3 py-1.5 rounded hover:bg-green-700 text-sm"
                    >
                      Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="bg-gray-500 text-white px-3 py-1.5 rounded hover:bg-gray-600 text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  // View Mode
                  <>
                    <span className="text-sm text-gray-700 dark:text-gray-200 font-medium">{item.name}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-base font-mono font-bold text-[#7c1d1d] dark:text-red-400 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded">
                        {item.code}
                      </span>
                      {isAdmin && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => startEdit(item)}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteCode(item.id)}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/* =========================================================
   CALENDAR
   ========================================================= */

function Calendar({ state, setState }) {
  const [cursor, setCursor] = useState(new Date());
  const [selected, setSelected] = useState(null);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  const first = new Date(year, month, 1);
  const startWeekday = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const selectedISO = selected ? todayISO(selected) : null;
  const events =
    selectedISO && state.events[selectedISO] ? state.events[selectedISO] : [];

  const addEvent = async (title) => {
    if (!selected) return;
    if (!title.trim()) return;

    // Add to state
    setState((s) => {
      const iso = todayISO(selected);
      const dayEvents = s.events[iso] ? [...s.events[iso]] : [];
      dayEvents.push({ id: uid(), title: title.trim() });
      const newState = {
        ...s,
        events: {
          ...s.events,
          [iso]: dayEvents,
        },
      };

      // Save to Supabase
      saveStateToSupabase(newState).catch(err => console.error('Error saving event:', err));

      return newState;
    });
  };

  const removeEvent = async (id) => {
    if (!selected) return;
    const iso = todayISO(selected);

    // Remove from state
    setState((s) => {
      const dayEvents = (s.events[iso] || []).filter((e) => e.id !== id);
      const newState = {
        ...s,
        events: {
          ...s.events,
          [iso]: dayEvents,
        },
      };

      // Save to Supabase
      saveStateToSupabase(newState).catch(err => console.error('Error removing event:', err));

      return newState;
    });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 md:p-4 border-t-4 border-[#7c1d1d] dark:border-red-600">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setCursor(new Date(year, month - 1, 1))}
          className="px-4 py-2 text-sm rounded border border-[#7c1d1d] dark:border-red-600 text-[#7c1d1d] dark:text-red-400 hover:bg-[#7c1d1d] hover:text-white font-medium transition-colors"
        >
          ← Prev
        </button>
        <h3 className="font-bold text-lg text-[#7c1d1d] dark:text-red-400">
          {cursor.toLocaleString(undefined, { month: "long", year: "numeric" })}
        </h3>
        <div className="flex gap-2">
          <button
            onClick={() => {
              const now = new Date();
              setCursor(now);
              setSelected(now);
            }}
            className="px-4 py-2 text-sm rounded border border-[#7c1d1d] dark:border-red-600 text-[#7c1d1d] dark:text-red-400 hover:bg-[#7c1d1d] hover:text-white font-medium transition-colors"
          >
            Today
          </button>
          <button
            onClick={() => setCursor(new Date(year, month + 1, 1))}
            className="px-4 py-2 text-sm rounded border border-[#7c1d1d] dark:border-red-600 text-[#7c1d1d] dark:text-red-400 hover:bg-[#7c1d1d] hover:text-white font-medium transition-colors"
          >
            Next →
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 text-xs mb-2 text-gray-600 dark:text-gray-300">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} className="p-0.5 text-center font-medium">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-2">
        {cells.map((d, i) => {
          const iso = d ? todayISO(d) : `x-${i}`;
          const dayEvents = d ? state.events[todayISO(d)] || [] : [];
          const isToday = d && todayISO(d) === todayISO(new Date());
          const isSelected =
            d && selected && todayISO(d) === todayISO(selected);
          return (
            <button
              key={iso}
              disabled={!d}
              onClick={() => d && setSelected(d)}
              className={`h-20 rounded border text-left p-2 overflow-hidden ${
                isSelected ? "ring-2 ring-[#7c1d1d] dark:ring-red-600" : ""
              } ${isToday ? "border-[#7c1d1d] dark:border-red-600 border-2" : "border-gray-200 dark:border-gray-600"} ${
                d ? "bg-white dark:bg-gray-700 hover:bg-red-50 dark:hover:bg-gray-600" : "bg-gray-50 dark:bg-gray-900 opacity-40"
              }`}
            >
              <div className="text-xs font-medium mb-1 dark:text-gray-200">
                {d ? d.getDate() : ""}
              </div>
              <div className="space-y-1">
                {dayEvents.slice(0, 2).map((ev) => (
                  <div
                    key={ev.id}
                    className="text-[10px] bg-[#7c1d1d] dark:bg-red-900 bg-opacity-10 dark:bg-opacity-30 text-[#7c1d1d] dark:text-red-300 rounded px-1 py-0.5 truncate font-medium"
                    title={ev.title}
                  >
                    {ev.title}
                  </div>
                ))}
                {dayEvents.length > 2 && (
                  <div className="text-[9px] text-gray-400 dark:text-gray-500">
                    +{dayEvents.length - 2}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {selected ? (
        <div className="mt-6 border-t dark:border-gray-600 pt-4">
          <h4 className="font-semibold mb-2 dark:text-gray-200">
            {selected.toLocaleDateString(undefined, {
              weekday: "long",
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </h4>
          <EventEditor events={events} onAdd={addEvent} onRemove={removeEvent} />
        </div>
      ) : null}
    </div>
  );
}

function EventEditor({ events, onAdd, onRemove }) {
  const [title, setTitle] = useState("");
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (onAdd(title), setTitle(""))}
          placeholder="Add event…"
          className="border dark:border-gray-600 rounded p-2 flex-1 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
        />
        <button
          onClick={() => {
            onAdd(title);
            setTitle("");
          }}
          className="bg-[#7c1d1d] dark:bg-red-900 text-white px-4 py-2 rounded-lg hover:bg-[#651818] dark:hover:bg-red-800 font-medium transition-colors"
        >
          Add
        </button>
      </div>
      <div className="space-y-2">
        {events.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400">No events for this day.</div>
        ) : (
          events.map((ev) => (
            <div
              key={ev.id}
              className="flex items-center justify-between border dark:border-gray-600 rounded p-2 dark:bg-gray-700"
            >
              <span className="dark:text-gray-200">{ev.title}</span>
              <button
                onClick={() => onRemove(ev.id)}
                className="text-red-600 dark:text-red-400 text-sm hover:underline"
              >
                Remove
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* =========================================================
   ATTENDANCE
   ========================================================= */

function Attendance({ state, setState, user }) {
  const [date, setDate] = useState(todayISO());
  const [session, setSession] = useState("AM");
  const [grade, setGrade] = useState(GRADES_AM[0]);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLocked, setIsLocked] = useState(state.attendanceLocked || false);

  const availableGrades = session === "AM" ? GRADES_AM : GRADES_PM;

  useEffect(() => {
    if (!availableGrades.includes(grade)) {
      setGrade(availableGrades[0]);
    }
  }, [session, grade, availableGrades]);

  // Load attendance from Supabase when component mounts
  useEffect(() => {
    async function loadAttendance() {
      try {
        const { data: attendanceData, error } = await supabase
          .from('attendance')
          .select('*')
          .eq('school_year', CURRENT_SCHOOL_YEAR);

        if (error) {
          console.error('❌ Error loading attendance:', error);
          return;
        }

        // Transform attendance from Supabase format to component format
        const attendance = {};
        (attendanceData || []).forEach(record => {
          if (!attendance[record.date]) {
            attendance[record.date] = {};
          }
          if (!attendance[record.date][record.grade]) {
            attendance[record.date][record.grade] = {};
          }

          // Find the student by name to get their ID
          const gradeStudents = state.studentsByGrade[record.grade] || [];
          const student = gradeStudents.find(s => s.name === record.student_name);

          if (student) {
            // Convert present (boolean) to status ("P", "A")
            const status = record.present ? "P" : "A";
            attendance[record.date][record.grade][student.id] = { status };
          }
        });

        // Update state with loaded attendance
        setState(s => ({
          ...s,
          attendance
        }));

        console.log('✅ Attendance loaded from Supabase');
      } catch (error) {
        console.error('❌ Error loading attendance:', error);
      }
    }

    loadAttendance();
  }, [state.studentsByGrade, setState]);

  // Set initial grade to user's assigned grade if they're a teacher
  useEffect(() => {
    if (user.role === "teacher" && user.gradeLevel) {
      // Normalize grade level - remove "Grade " prefix if present
      let normalizedGrade = user.gradeLevel.replace(/^Grade\s+/i, '');

      // Check if normalized grade exists in AM or PM grades
      if (GRADES_AM.includes(normalizedGrade)) {
        setGrade(normalizedGrade);
        setSession("AM");
      } else if (GRADES_PM.includes(normalizedGrade)) {
        setGrade(normalizedGrade);
        setSession("PM");
      } else if (GRADES_PM.includes(user.gradeLevel)) {
        // Original grade is in PM
        setGrade(user.gradeLevel);
        setSession("PM");
      } else if (GRADES_AM.includes(user.gradeLevel)) {
        // Original grade is in AM
        setGrade(user.gradeLevel);
        setSession("AM");
      }
    }
  }, [user]);

  const students = state.studentsByGrade[grade] || [];

  const attendanceFor = useMemo(() => {
    return state.attendance[date]?.[grade] ?? {};
  }, [state.attendance, date, grade]);

  const setStatus = (studentId, status) => {
    setState((s) => {
      const day = s.attendance[date] ? { ...s.attendance[date] } : {};
      const byGrade = day[grade] ? { ...day[grade] } : {};
      byGrade[studentId] = { ...(byGrade[studentId] || {}), status };
      day[grade] = byGrade;
      return {
        ...s,
        attendance: {
          ...s.attendance,
          [date]: day,
        },
      };
    });
  };

  const presentCount = students.filter(
    (st) => attendanceFor[st.id]?.status === "P"
  ).length;
  const absentCount = students.filter(
    (st) => attendanceFor[st.id]?.status === "A"
  ).length;
  const lateCount = students.filter(
    (st) => attendanceFor[st.id]?.status === "L"
  ).length;

  // Admins can edit all grades, teachers can only edit their assigned grade
  // Normalize both grade values to handle "Grade 6A" vs "6A" mismatch
  // Teachers cannot edit if attendance is locked
  const normalizedUserGrade = user.gradeLevel?.replace(/^Grade\s+/i, '') || '';
  const canEdit = user.role === "admin" ? true :
    (user.role === "teacher" && !isLocked && (user.gradeLevel === grade || normalizedUserGrade === grade));

  const submitAttendance = async () => {
    if (!canEdit) {
      alert("You don't have permission to submit attendance for this class.");
      return;
    }

    setIsSaving(true);
    try {
      console.log(`💾 Submitting attendance for ${grade} on ${date}...`);

      // Save the entire state to Supabase (which includes attendance)
      await saveStateToSupabase(state, user);

      console.log('✅ Attendance submitted successfully');
      alert('✅ Attendance submitted successfully! Admins can now view this data.');
    } catch (error) {
      console.error('❌ Error submitting attendance:', error);
      alert('Failed to submit attendance. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const refreshAttendance = async () => {
    setIsRefreshing(true);
    try {
      console.log('🔄 Refreshing attendance from Supabase...');

      // Fetch attendance from Supabase
      const { data: attendanceData, error: attendanceError } = await supabase
        .from('attendance')
        .select('*')
        .eq('school_year', CURRENT_SCHOOL_YEAR);

      if (attendanceError) {
        console.error('❌ Error fetching attendance:', attendanceError);
        alert('Failed to refresh attendance. Please try again.');
        return;
      }

      // Transform attendance from Supabase format to component format
      // Supabase: array of { date, grade, session, student_name, present }
      // Component: { date: { grade: { studentId: { status } } } }
      const attendance = {};
      (attendanceData || []).forEach(record => {
        if (!attendance[record.date]) {
          attendance[record.date] = {};
        }
        if (!attendance[record.date][record.grade]) {
          attendance[record.date][record.grade] = {};
        }

        // Find the student by name to get their ID
        const gradeStudents = state.studentsByGrade[record.grade] || [];
        const student = gradeStudents.find(s => s.name === record.student_name);

        if (student) {
          // Convert present (boolean) to status ("P", "A")
          const status = record.present ? "P" : "A";
          attendance[record.date][record.grade][student.id] = { status };
        }
      });

      // Update state with refreshed attendance data
      setState(s => ({
        ...s,
        attendance
      }));

      console.log('✅ Attendance refreshed successfully');
    } catch (error) {
      console.error('❌ Error refreshing attendance:', error);
      alert('Failed to refresh attendance. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  const toggleLock = () => {
    const newLockState = !isLocked;
    setIsLocked(newLockState);
    setState(s => ({
      ...s,
      attendanceLocked: newLockState
    }));
    console.log(`🔒 Attendance ${newLockState ? 'locked' : 'unlocked'}`);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 md:p-4 border-t-4 border-[#7c1d1d] dark:border-red-600">
      <h2 className="text-base font-semibold mb-3 text-[#7c1d1d] dark:text-red-400">
        Attendance
      </h2>

      {user.role === "teacher" && user.gradeLevel !== grade && (
        <div className="mb-2 p-2 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded text-xs text-yellow-800 dark:text-yellow-200">
          ⚠️ You can only edit attendance for your assigned grade: <b>{user.gradeLevel}</b>
        </div>
      )}

      {isLocked && user.role === "teacher" && (
        <div className="mb-2 p-2 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-xs text-red-800 dark:text-red-200">
          🔒 Attendance is locked. You cannot make changes until an admin unlocks it.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
        <div>
          <label className="block text-[10px] font-medium mb-0.5 dark:text-gray-300">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="border dark:border-gray-600 rounded p-1 w-full text-xs dark:bg-gray-700 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-[10px] font-medium mb-0.5 dark:text-gray-300">Class</label>
          <select
            value={grade}
            onChange={(e) => setGrade(e.target.value)}
            className="border dark:border-gray-600 rounded p-1 w-full text-xs dark:bg-gray-700 dark:text-white"
            disabled={user.role === "teacher"}
          >
            {availableGrades.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
          {user.role === "teacher" && (
            <p className="text-[9px] text-gray-500 dark:text-gray-400 mt-0.5">Grade locked to: {user.gradeLevel}</p>
          )}
        </div>
        <div>
          <label className="block text-[10px] font-medium mb-0.5 dark:text-gray-300">Session</label>
          <select
            value={session}
            onChange={(e) => setSession(e.target.value)}
            className="border dark:border-gray-600 rounded p-1 w-full text-xs dark:bg-gray-700 dark:text-white"
            disabled={user.role === "teacher"}
          >
            {SESSIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {user.role === "teacher" && (
            <p className="text-[9px] text-gray-500 dark:text-gray-400 mt-0.5">Session locked to your grade</p>
          )}
        </div>
      </div>

      <div className="mb-2 text-xs text-gray-700 dark:text-gray-300">
        <b>Summary:</b> Present {presentCount} • Absent {absentCount} • Late{" "}
        {lateCount} : Total {students.length}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700 text-left">
              <th className="p-1 border dark:border-gray-600 w-10 dark:text-gray-200">#</th>
              <th className="p-1 border dark:border-gray-600 dark:text-gray-200">Student</th>
              <th className="p-1 border dark:border-gray-600 w-40 dark:text-gray-200">Status</th>
            </tr>
          </thead>
          <tbody>
            {students.length === 0 ? (
              <tr>
                <td colSpan={3} className="p-2 text-center text-gray-500 dark:text-gray-400">
                  No students in this class yet.
                </td>
              </tr>
            ) : (
              students.map((st, index) => (
                <tr key={st.id} className="odd:bg-white even:bg-gray-50 dark:odd:bg-gray-800 dark:even:bg-gray-700">
                  <td className="p-1 border dark:border-gray-600 dark:text-gray-200">{index + 1}</td>
                  <td className="p-1 border dark:border-gray-600 dark:text-gray-200">{st.name}</td>
                  <td className="p-1 border dark:border-gray-600">
                    {canEdit ? (
                      <select
                        value={attendanceFor[st.id]?.status || ""}
                        onChange={(e) => setStatus(st.id, e.target.value)}
                        className="border dark:border-gray-600 rounded p-0.5 w-full text-xs dark:bg-gray-700 dark:text-white"
                      >
                        <option value="">— select —</option>
                        <option value="P">Present</option>
                        <option value="A">Absent</option>
                        <option value="L">Late</option>
                      </select>
                    ) : (
                      <span className="text-gray-600 dark:text-gray-300">
                        {attendanceFor[st.id]?.status === "P"
                          ? "Present"
                          : attendanceFor[st.id]?.status === "A"
                          ? "Absent"
                          : attendanceFor[st.id]?.status === "L"
                          ? "Late"
                          : "—"}
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        {user.role === "admin" && (
          <>
            <button
              onClick={toggleLock}
              className={`px-4 py-2 rounded font-medium text-white transition-colors ${
                isLocked
                  ? 'bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800'
                  : 'bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800'
              }`}
            >
              {isLocked ? '🔒 Unlock Attendance' : '🔓 Lock Attendance'}
            </button>
            <button
              onClick={refreshAttendance}
              disabled={isRefreshing}
              className={`px-4 py-2 rounded font-medium text-white transition-colors ${
                isRefreshing
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800'
              }`}
            >
              {isRefreshing ? 'Refreshing...' : 'Refresh Attendance'}
            </button>
          </>
        )}
        {canEdit && (
          <button
            onClick={submitAttendance}
            disabled={isSaving}
            className={`px-4 py-2 rounded font-medium text-white transition-colors ${
              isSaving
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-[#7c1d1d] hover:bg-[#651818] dark:bg-red-700 dark:hover:bg-red-800'
            }`}
          >
            {isSaving ? 'Submitting...' : 'Submit Attendance'}
          </button>
        )}
      </div>
    </div>
  );
}

/* =========================================================
   TEACHERS VIEW
   ========================================================= */

function TeachersView() {
  const teacherAssignments = [
    { grade: "PreK", amA_teacher: "Samar Shehata", amA_assistant: "Monica Soliman", amA_room: "101", amB_teacher: "Rowida Bahnan", amB_assistant: "Nada Bahnan", amB_room: "102", pm_teacher: "Samar Shehata", pm_assistant: "Monica Soliman", pm_room: "101", online_teacher: "" },
    { grade: "K", amA_teacher: "Nevine Rofail", amA_assistant: "Hannah Aziz", amA_room: "103", amB_teacher: "Kristina Awad", amB_assistant: "Youstina Kamel", amB_room: "104", pm_teacher: "Merna Hanna", pm_assistant: "Christine Kamel", pm_room: "103", online_teacher: "" },
    { grade: "1", amA_teacher: "Youstina Kamel", amA_assistant: "Maya Abdo", amA_room: "105", amB_teacher: "Mary Salama", amB_assistant: "Mariam Halim", amB_room: "106", pm_teacher: "Mary Salama", pm_assistant: "Mariam Halim", pm_room: "105", online_teacher: "" },
    { grade: "2", amA_teacher: "Merna Hanna", amA_assistant: "Christine Kamel", amA_room: "107", amB_teacher: "Sarah Youssef", amB_assistant: "Nour Abdel-Malak", amB_room: "108", pm_teacher: "Sarah Youssef", pm_assistant: "Nour Abdel-Malak", pm_room: "107", online_teacher: "" },
    { grade: "3", amA_teacher: "Rana Awad", amA_assistant: "Mariam Mikhail", amA_room: "109", amB_teacher: "Marian Gergis", amB_assistant: "Veronia Aziz", amB_room: "110", pm_teacher: "Marian Gergis", pm_assistant: "Veronia Aziz", pm_room: "109", online_teacher: "" },
    { grade: "4", amA_teacher: "Marina Aziz", amA_assistant: "Nardine Youssef", amA_room: "111", amB_teacher: "Christine Ibrahim", amB_assistant: "Monica Hanna", amB_room: "112", pm_teacher: "Christine Ibrahim", pm_assistant: "Monica Hanna", pm_room: "111", online_teacher: "" },
    { grade: "5", amA_teacher: "Bishoy Awad", amA_assistant: "Mina Aziz", amA_room: "113", amB_teacher: "Kyrillos Hanna", amB_assistant: "Mark Youssef", amB_room: "114", pm_teacher: "Kyrillos Hanna", pm_assistant: "Mark Youssef", pm_room: "113", online_teacher: "" },
    { grade: "6A", amA_teacher: "Mary Mikhail", amA_assistant: "Youstina Awad", amA_room: "115", amB_teacher: "Marian Salama", amB_assistant: "Sarah Gergis", amB_room: "116", pm_teacher: "Mary Mikhail", pm_assistant: "Youstina Awad", pm_room: "115", online_teacher: "" },
    { grade: "6B", amA_teacher: "Amir Aziz", amA_assistant: "Fady Hanna", amA_room: "117", amB_teacher: "Bishoy Youssef", amB_assistant: "Mina Ibrahim", amB_room: "118", pm_teacher: "Amir Aziz", pm_assistant: "Fady Hanna", pm_room: "117", online_teacher: "" },
    { grade: "7A", amA_teacher: "Christine Salama", amA_assistant: "Monica Mikhail", amA_room: "119", amB_teacher: "Merna Gergis", amB_assistant: "Sarah Awad", amB_room: "120", pm_teacher: "Christine Salama", pm_assistant: "Monica Mikhail", pm_room: "119", online_teacher: "" },
    { grade: "7B", amA_teacher: "Mark Hanna", amA_assistant: "Kyrillos Ibrahim", amA_room: "121", amB_teacher: "Mina Youssef", amB_assistant: "Bishoy Aziz", amB_room: "122", pm_teacher: "Mark Hanna", pm_assistant: "Kyrillos Ibrahim", pm_room: "121", online_teacher: "" },
    { grade: "8A", amA_teacher: "Mariam Awad", amA_assistant: "Youstina Salama", amA_room: "123", amB_teacher: "Sarah Mikhail", amB_assistant: "Marian Hanna", amB_room: "124", pm_teacher: "Mariam Awad", pm_assistant: "Youstina Salama", pm_room: "123", online_teacher: "" },
    { grade: "8B", amA_teacher: "Fady Aziz", amA_assistant: "Amir Youssef", amA_room: "125", amB_teacher: "Bishoy Gergis", amB_assistant: "Mina Hanna", amB_room: "126", pm_teacher: "Fady Aziz", pm_assistant: "Amir Youssef", pm_room: "125", online_teacher: "" },
  ];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-700">
            <th className="p-2 border dark:border-gray-600 text-left font-semibold dark:text-gray-200">Grade</th>
            <th className="p-2 border dark:border-gray-600 text-left font-semibold dark:text-gray-200">Teacher (AM A)</th>
            <th className="p-2 border dark:border-gray-600 text-left font-semibold dark:text-gray-200">Assistant (AM A)</th>
            <th className="p-2 border dark:border-gray-600 text-left font-semibold dark:text-gray-200">Room # (AM A)</th>
            <th className="p-2 border dark:border-gray-600 text-left font-semibold dark:text-gray-200">Teacher (AM B)</th>
            <th className="p-2 border dark:border-gray-600 text-left font-semibold dark:text-gray-200">Assistant (AM B)</th>
            <th className="p-2 border dark:border-gray-600 text-left font-semibold dark:text-gray-200">Room # (AM B)</th>
            <th className="p-2 border dark:border-gray-600 text-left font-semibold dark:text-gray-200">Teacher (PM)</th>
            <th className="p-2 border dark:border-gray-600 text-left font-semibold dark:text-gray-200">Assistant (PM)</th>
            <th className="p-2 border dark:border-gray-600 text-left font-semibold dark:text-gray-200">Room # (PM)</th>
            <th className="p-2 border dark:border-gray-600 text-left font-semibold dark:text-gray-200">Teacher (ONLINE)</th>
          </tr>
        </thead>
        <tbody>
          {teacherAssignments.map((assignment) => (
            <tr key={assignment.grade} className="odd:bg-white dark:odd:bg-gray-800 even:bg-gray-50 dark:even:bg-gray-700">
              <td className="p-2 border dark:border-gray-600 font-medium text-gray-900 dark:text-gray-200">{assignment.grade}</td>
              <td className="p-2 border dark:border-gray-600 text-gray-900 dark:text-gray-200">{assignment.amA_teacher}</td>
              <td className="p-2 border dark:border-gray-600 text-gray-900 dark:text-gray-200">{assignment.amA_assistant}</td>
              <td className="p-2 border dark:border-gray-600 text-gray-900 dark:text-gray-200">{assignment.amA_room}</td>
              <td className="p-2 border dark:border-gray-600 text-gray-900 dark:text-gray-200">{assignment.amB_teacher}</td>
              <td className="p-2 border dark:border-gray-600 text-gray-900 dark:text-gray-200">{assignment.amB_assistant}</td>
              <td className="p-2 border dark:border-gray-600 text-gray-900 dark:text-gray-200">{assignment.amB_room}</td>
              <td className="p-2 border dark:border-gray-600 text-gray-900 dark:text-gray-200">{assignment.pm_teacher}</td>
              <td className="p-2 border dark:border-gray-600 text-gray-900 dark:text-gray-200">{assignment.pm_assistant}</td>
              <td className="p-2 border dark:border-gray-600 text-gray-900 dark:text-gray-200">{assignment.pm_room}</td>
              <td className="p-2 border dark:border-gray-600 text-gray-900 dark:text-gray-200">{assignment.online_teacher}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* =========================================================
   STUDENTS
   ========================================================= */

function Students({ state, setState, user }) {
  const [viewMode, setViewMode] = useState("students"); // "students" or "teachers"
  const [session, setSession] = useState("AM");
  const [grade, setGrade] = useState(GRADES_AM[0]);
  const [name, setName] = useState("");
  const [showParentInfo, setShowParentInfo] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [movingStudent, setMovingStudent] = useState(null); // { st, grade }

  const availableGrades = session === "AM" ? GRADES_AM : GRADES_PM;

  useEffect(() => {
    if (!availableGrades.includes(grade)) {
      setGrade(availableGrades[0]);
    }
  }, [session, grade, availableGrades]);

  // Parent data is now imported from Supabase directly
  // No need for auto-import from CSV on mount

  const students = state.studentsByGrade[grade] || [];

  const query = search.trim().toLowerCase();

  // Searches every class in both sessions, so a student can be found without
  // knowing which room they were placed in.
  const searchResults = useMemo(() => {
    if (!query) return [];
    const hits = [];
    Object.keys(state.studentsByGrade || {}).forEach((g) => {
      (state.studentsByGrade[g] || []).forEach((st) => {
        if (st.name && st.name.toLowerCase().includes(query)) {
          hits.push({ student: st, grade: g });
        }
      });
    });
    return hits.sort((a, b) => a.student.name.localeCompare(b.student.name));
  }, [query, state.studentsByGrade]);

  const canEdit = user.role === "admin";

  const downloadGradePdf = async () => {
    setPdfBusy(true);
    try {
      const staff = GRADE_STAFF[grade] || {};
      const staffBits = [
        staff.room ? `Room ${staff.room}` : null,
        staff.lead ? `Lead: ${staff.lead}` : null,
        staff.teacher ? `Teacher: ${staff.teacher}` : null,
        staff.teachers ? `Teachers: ${staff.teachers}` : null,
        staff.ta ? `TA: ${staff.ta}` : null,
      ].filter(Boolean);

      await downloadRosterPdf({
        title: `${grade} Roster`,
        subtitle: [
          `MAPS Sunday School ${CURRENT_SCHOOL_YEAR}`,
          `${session} session`,
          `${students.length} student${students.length === 1 ? "" : "s"}`,
          ...staffBits,
        ].join("  ·  "),
        students,
        filename: `${pdfSafeName(grade)}-roster-${CURRENT_SCHOOL_YEAR}.pdf`,
      });
    } catch (err) {
      console.error("Failed to build roster PDF:", err);
      alert("Could not generate the PDF. Please try again.");
    } finally {
      setPdfBusy(false);
    }
  };

  const addStudent = async () => {
    if (!canEdit) {
      alert("Only administrators can modify student rosters.");
      return;
    }
    const n = name.trim();
    if (!n) return;

    // Add to state
    setState((s) => {
      const copy = { ...s.studentsByGrade };
      const list = copy[grade] ? [...copy[grade]] : [];
      list.push({ id: uid(), name: n });
      copy[grade] = list;
      const newState = { ...s, studentsByGrade: copy };

      // Save to Supabase
      saveStateToSupabase(newState).catch(err => console.error('Error saving student:', err));

      return newState;
    });
    setName("");
  };

  const removeStudent = async (id) => {
    if (!canEdit) {
      alert("Only administrators can modify student rosters.");
      return;
    }
    if (!window.confirm("Remove this student from the roster?")) return;

    // Remove from state
    setState((s) => {
      const copy = { ...s.studentsByGrade };
      copy[grade] = (copy[grade] || []).filter((st) => st.id !== id);
      const newState = { ...s, studentsByGrade: copy };

      // Save to Supabase
      saveStateToSupabase(newState).catch(err => console.error('Error removing student:', err));

      return newState;
    });
  };

  // studentGrade is explicit because a search hit can belong to a class other
  // than the selected one; defaulting to `grade` would write to the wrong list.
  // Carries the whole student object across, so parent contacts, allergies and
  // medical notes travel with the child rather than being re-keyed.
  const moveStudent = async (student, fromGrade, toGrade) => {
    if (!canEdit) {
      alert("Only administrators can modify student rosters.");
      return;
    }
    if (!toGrade || toGrade === fromGrade) return;

    setState((s) => {
      const copy = { ...s.studentsByGrade };
      const source = (copy[fromGrade] || []).filter((st) => st.id !== student.id);
      const moved = (copy[fromGrade] || []).find((st) => st.id === student.id);
      if (!moved) return s;
      copy[fromGrade] = source;
      copy[toGrade] = [...(copy[toGrade] || []), moved];
      const newState = { ...s, studentsByGrade: copy };

      saveStateToSupabase(newState).catch((err) => console.error('Error moving student:', err));

      return newState;
    });
    setMovingStudent(null);
  };

  const updateParentInfo = async (studentId, parentInfo, studentGrade = grade) => {
    console.log('📝 Updating parent info for student:', studentId, parentInfo);

    // Update in state
    setState((s) => {
      const copy = { ...s.studentsByGrade };
      const list = copy[studentGrade] ? [...copy[studentGrade]] : [];
      const studentIndex = list.findIndex((st) => st.id === studentId);
      if (studentIndex !== -1) {
        list[studentIndex] = { ...list[studentIndex], parentInfo };
        console.log('✅ Parent info updated in state for:', list[studentIndex].name);
      }
      copy[studentGrade] = list;
      const newState = { ...s, studentsByGrade: copy };

      // Save to Supabase
      saveStateToSupabase(newState).catch(err => console.error('Error updating parent info:', err));

      return newState;
    });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 md:p-4 border-t-4 border-[#7c1d1d] dark:border-red-600">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-[#7c1d1d] dark:text-red-400">
          {canEdit ? (
            viewMode === "students" ? "Students" : "Teachers"
          ) : (
            "Students"
          )}
        </h2>

        {/* Admin View Toggle */}
        {canEdit && (
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode("students")}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                viewMode === "students"
                  ? "bg-[#7c1d1d] text-white"
                  : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
              }`}
            >
              Students
            </button>
            <button
              onClick={() => setViewMode("teachers")}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                viewMode === "teachers"
                  ? "bg-[#7c1d1d] text-white"
                  : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
              }`}
            >
              Teachers
            </button>
          </div>
        )}
      </div>

      {viewMode === "teachers" ? (
        // Teachers View
        <TeachersView />
      ) : (
        // Students View (existing code)
        <>
          {!canEdit && (
            <div className="mb-2 p-2 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded text-xs text-yellow-800 dark:text-yellow-200">
              ⚠️ View-only mode. Only administrators can modify student rosters.
            </div>
          )}

      {/* Student search, across every class */}
      <div className="mb-2">
        <label className="block text-sm font-medium mb-1 dark:text-gray-300">
          Find a student
        </label>
        <div className="flex gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search all classes by student name"
            className="border dark:border-gray-600 rounded p-2 flex-1 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
          />
          {query && (
            <button
              onClick={() => setSearch("")}
              className="px-3 py-1 rounded text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 whitespace-nowrap"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Teacher/Room Info */}
      {GRADE_STAFF[grade] && (
        <div className="mb-2 p-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded">
          <div className="flex items-center gap-2 text-xs flex-wrap">
            {GRADE_STAFF[grade].room && (
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Room:</span>{" "}
                <span className="text-gray-900 dark:text-white">#{GRADE_STAFF[grade].room}</span>
              </div>
            )}
            {ROOM_CAPACITY[grade] !== undefined && (
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Seats:</span>{" "}
                <span className="text-gray-900 dark:text-white">
                  {students.length}/{ROOM_CAPACITY[grade]}
                </span>
                {students.length > ROOM_CAPACITY[grade] && (
                  <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300">
                    OVERFLOW +{students.length - ROOM_CAPACITY[grade]}
                  </span>
                )}
              </div>
            )}
            {GRADE_STAFF[grade].lead && (
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Lead:</span>{" "}
                <span className="text-gray-900 dark:text-white">{GRADE_STAFF[grade].lead}</span>
              </div>
            )}
            {GRADE_STAFF[grade].teacher && (
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Teacher:</span>{" "}
                <span className="text-gray-900 dark:text-white">{GRADE_STAFF[grade].teacher}</span>
              </div>
            )}
            {GRADE_STAFF[grade].teachers && (
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">Teachers:</span>{" "}
                <span className="text-gray-900 dark:text-white">{GRADE_STAFF[grade].teachers}</span>
              </div>
            )}
            {GRADE_STAFF[grade].ta && (
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">TA:</span>{" "}
                <span className="text-gray-900 dark:text-white">{GRADE_STAFF[grade].ta}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
        <div>
          <label className="block text-sm font-medium mb-1 dark:text-gray-300">Session</label>
          <select
            value={session}
            onChange={(e) => setSession(e.target.value)}
            className="border dark:border-gray-600 rounded p-2 w-full dark:bg-gray-700 dark:text-white"
          >
            {SESSIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1 dark:text-gray-300">Class</label>
          <select
            value={grade}
            onChange={(e) => setGrade(e.target.value)}
            className="border dark:border-gray-600 rounded p-2 w-full dark:bg-gray-700 dark:text-white"
          >
            {availableGrades.map((g) => {
              return (
                <option key={g} value={g}>
                  {g}
                </option>
              );
            })}
          </select>
        </div>
        {canEdit && (
          <div>
            <label className="block text-sm font-medium mb-1 dark:text-gray-300">
              New student name
            </label>
            <div className="flex gap-2">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addStudent()}
                placeholder="Full name"
                className="border dark:border-gray-600 rounded p-2 flex-1 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
              />
              <button
                onClick={addStudent}
                className="bg-[#7c1d1d] dark:bg-red-700 text-white px-2 py-1 rounded hover:bg-[#651818] dark:hover:bg-red-800 whitespace-nowrap font-medium transition-colors text-xs"
              >
                Add
              </button>
            </div>
          </div>
        )}
      </div>

      {query ? (
        <>
          <div className="mb-2 text-xs text-gray-600 dark:text-gray-400">
            {searchResults.length} match{searchResults.length === 1 ? "" : "es"} for "{search.trim()}"
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-700 text-left">
                  <th className="p-2 border dark:border-gray-600 dark:text-gray-200">Student</th>
                  <th className="p-2 border dark:border-gray-600 w-36 dark:text-gray-200">Class</th>
                  <th className="p-2 border dark:border-gray-600 dark:text-gray-200">Parent</th>
                  <th className="p-2 border dark:border-gray-600 w-32 dark:text-gray-200">Phone</th>
                  <th className="p-2 border dark:border-gray-600 w-32 dark:text-gray-200">Parent Info</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-4 text-center text-gray-500 dark:text-gray-400">
                      No student matches "{search.trim()}".
                    </td>
                  </tr>
                ) : (
                  searchResults.map(({ student: st, grade: g }) => (
                    <tr key={`${g}-${st.id}`} className="odd:bg-white dark:odd:bg-gray-800 even:bg-gray-50 dark:even:bg-gray-700">
                      <td className="p-2 border dark:border-gray-600 text-gray-900 dark:text-gray-200">
                        {st.name}
                        {st.parentInfo?.allergies && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300" title={st.parentInfo.allergies}>
                            ⚠ Allergies
                          </span>
                        )}
                        {st.parentInfo?.medicalConditions && (
                          <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300" title={st.parentInfo.medicalConditions}>
                            ⚕ Medical
                          </span>
                        )}
                      </td>
                      <td className="p-2 border dark:border-gray-600">
                        <button
                          onClick={() => {
                            setSession(GRADES_PM.includes(g) ? "PM" : "AM");
                            setGrade(g);
                            setSearch("");
                          }}
                          className="text-[#7c1d1d] dark:text-red-400 hover:underline font-medium"
                          title={`Open the ${g} roster`}
                        >
                          {g}
                        </button>
                        {GRADE_STAFF[g] && GRADE_STAFF[g].room && (
                          <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">#{GRADE_STAFF[g].room}</span>
                        )}
                      </td>
                      <td className="p-2 border dark:border-gray-600 text-gray-900 dark:text-gray-200">
                        {st.parentInfo?.name || <span className="text-gray-400 dark:text-gray-500">-</span>}
                      </td>
                      <td className="p-2 border dark:border-gray-600 text-gray-900 dark:text-gray-200">
                        {st.parentInfo?.phone || <span className="text-gray-400 dark:text-gray-500">-</span>}
                      </td>
                      <td className="p-2 border dark:border-gray-600">
                        <button
                          onClick={() => setShowParentInfo({ st, grade: g })}
                          className="text-[#7c1d1d] dark:text-red-400 hover:underline font-medium text-sm"
                        >
                          📞 {canEdit ? "View/Edit" : "View"}
                        </button>
                        {canEdit && (
                          <button
                            onClick={() => setMovingStudent({ st, grade: g })}
                            className="ml-2 text-[#7c1d1d] dark:text-red-400 hover:underline font-medium text-sm"
                          >
                            Move
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
      <>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-gray-600 dark:text-gray-400">
          {students.length} student{students.length === 1 ? "" : "s"} in {grade}
        </div>
        <button
          onClick={downloadGradePdf}
          disabled={students.length === 0 || pdfBusy}
          className="bg-[#7c1d1d] dark:bg-red-700 text-white px-3 py-1.5 rounded text-xs font-medium hover:bg-[#651818] dark:hover:bg-red-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {pdfBusy ? "Preparing..." : "⬇ Download roster PDF"}
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700 text-left">
              <th className="p-2 border dark:border-gray-600 w-14 dark:text-gray-200">#</th>
              <th className="p-2 border dark:border-gray-600 dark:text-gray-200">Student</th>
              <th className="p-2 border dark:border-gray-600 w-32 dark:text-gray-200">Parent Info</th>
              {canEdit && <th className="p-2 border dark:border-gray-600 w-36 dark:text-gray-200">Action</th>}
            </tr>
          </thead>
          <tbody>
            {students.length === 0 ? (
              <tr>
                <td colSpan={canEdit ? 4 : 3} className="p-4 text-center text-gray-500 dark:text-gray-400">
                  No students yet.
                </td>
              </tr>
            ) : (
              students.map((st, index) => (
                <tr key={st.id} className="odd:bg-white dark:odd:bg-gray-800 even:bg-gray-50 dark:even:bg-gray-700">
                  <td className="p-2 border dark:border-gray-600 text-gray-900 dark:text-gray-200">{index + 1}</td>
                  <td className="p-2 border dark:border-gray-600 text-gray-900 dark:text-gray-200">
                    {st.name}
                    {st.parentInfo?.allergies && (
                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300" title={st.parentInfo.allergies}>
                        ⚠ Allergies
                      </span>
                    )}
                    {st.parentInfo?.medicalConditions && (
                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300" title={st.parentInfo.medicalConditions}>
                        ⚕ Medical
                      </span>
                    )}
                  </td>
                  <td className="p-2 border dark:border-gray-600">
                    <button
                      onClick={() => setShowParentInfo({ st, grade })}
                      className="text-[#7c1d1d] dark:text-red-400 hover:underline font-medium text-sm"
                    >
                      📞 {canEdit ? "View/Edit" : "View"}
                    </button>
                  </td>
                  {canEdit && (
                    <td className="p-2 border dark:border-gray-600">
                      <button
                        onClick={() => setMovingStudent({ st, grade })}
                        className="text-[#7c1d1d] dark:text-red-400 hover:underline font-medium"
                      >
                        Move
                      </button>
                      <button
                        onClick={() => removeStudent(st.id)}
                        className="ml-2 text-red-600 dark:text-red-400 hover:underline"
                      >
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      </>
      )}

      {movingStudent && (
        <MoveStudentModal
          student={movingStudent.st}
          fromGrade={movingStudent.grade}
          studentsByGrade={state.studentsByGrade}
          onClose={() => setMovingStudent(null)}
          onMove={(toGrade) => moveStudent(movingStudent.st, movingStudent.grade, toGrade)}
        />
      )}

      {/* Parent Info Modal */}
      {showParentInfo && (
        <ParentInfoModal
          student={showParentInfo.st}
          onClose={() => setShowParentInfo(null)}
          onSave={(parentInfo) => {
            updateParentInfo(showParentInfo.st.id, parentInfo, showParentInfo.grade);
            setShowParentInfo(null);
          }}
          canEdit={canEdit}
        />
      )}
        </>
      )}
    </div>
  );
}

/* =========================================================
   MOVE STUDENT MODAL
   ========================================================= */

function MoveStudentModal({ student, fromGrade, studentsByGrade, onClose, onMove }) {
  const [toGrade, setToGrade] = useState("");

  // Seat counts are advisory here, the same as on the roster: a full class is
  // labelled, not blocked, because the roster is the roster and the room is a
  // scheduling problem for a human.
  const describe = (g) => {
    const count = (studentsByGrade[g] || []).length;
    const cap = ROOM_CAPACITY[g];
    if (cap === undefined) return `${g} (${count})`;
    if (count >= cap) return `${g} (${count}/${cap}, full)`;
    return `${g} (${count}/${cap})`;
  };

  const destinations = ALL_GRADES.filter((g) => g !== fromGrade);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md p-6 border-t-4 border-[#7c1d1d] dark:border-red-600">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-xl font-bold text-[#7c1d1d] dark:text-red-400">Move student</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
            ×
          </button>
        </div>

        <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
          <span className="font-semibold">{student.name}</span> is in{" "}
          <span className="font-semibold">{fromGrade}</span>.
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          Parent contacts, allergies and medical notes move with them.
        </p>

        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Move to
        </label>
        <select
          value={toGrade}
          onChange={(e) => setToGrade(e.target.value)}
          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg p-2.5 mb-5 focus:ring-2 focus:ring-[#7c1d1d] focus:border-transparent"
        >
          <option value="">Choose a class...</option>
          {destinations.map((g) => (
            <option key={g} value={g}>
              {describe(g)}
            </option>
          ))}
        </select>

        <div className="flex gap-2">
          <button
            onClick={() => onMove(toGrade)}
            disabled={!toGrade}
            className="flex-1 bg-[#7c1d1d] dark:bg-red-700 text-white px-4 py-2 rounded-lg hover:bg-[#651818] dark:hover:bg-red-800 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Move
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-200 font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   PARENT INFO MODAL
   ========================================================= */

function ParentInfoModal({ student, onClose, onSave, canEdit }) {
  const [parentName, setParentName] = useState(student.parentInfo?.name || "");
  const [parentEmail, setParentEmail] = useState(student.parentInfo?.email || "");
  const [parentPhone, setParentPhone] = useState(student.parentInfo?.phone || "");
  const [parent2Name, setParent2Name] = useState(student.parentInfo?.parent2Name || "");
  const [parent2Email, setParent2Email] = useState(student.parentInfo?.parent2Email || "");
  const [parent2Phone, setParent2Phone] = useState(student.parentInfo?.parent2Phone || "");
  const [allergies, setAllergies] = useState(student.parentInfo?.allergies || "");
  const [medicalConditions, setMedicalConditions] = useState(student.parentInfo?.medicalConditions || "");
  const [address, setAddress] = useState(student.parentInfo?.address || "");

  // Every field the record can hold is listed here. onSave replaces the whole
  // parentInfo object, so anything omitted is destroyed on the first edit.
  const handleSave = () => {
    onSave({
      name: parentName.trim(),
      email: parentEmail.trim(),
      phone: parentPhone.trim(),
      parent2Name: parent2Name.trim(),
      parent2Email: parent2Email.trim(),
      parent2Phone: parent2Phone.trim(),
      allergies: allergies.trim(),
      medicalConditions: medicalConditions.trim(),
      address: address.trim(),
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-xl font-bold text-[#7c1d1d] dark:text-red-400">
            Parent Information
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
          <p className="font-semibold text-gray-800 dark:text-gray-200">Student: {student.name}</p>
        </div>

        <div className="space-y-4">
          <h4 className="font-semibold text-gray-700 dark:text-gray-300 border-b dark:border-gray-600 pb-2">Parent/Guardian 1</h4>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Parent Name
            </label>
            <input
              type="text"
              value={parentName}
              onChange={(e) => setParentName(e.target.value)}
              disabled={!canEdit}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] dark:focus:ring-red-600 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white"
              placeholder="Full name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email Address
            </label>
            <input
              type="email"
              value={parentEmail}
              onChange={(e) => setParentEmail(e.target.value)}
              disabled={!canEdit}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] dark:focus:ring-red-600 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white"
              placeholder="parent@example.com"
            />
            {parentEmail && (
              <a
                href={`mailto:${parentEmail}`}
                className="text-sm text-[#7c1d1d] dark:text-red-400 hover:underline mt-1 inline-block"
              >
                📧 Send Email
              </a>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              value={parentPhone}
              onChange={(e) => setParentPhone(e.target.value)}
              disabled={!canEdit}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] dark:focus:ring-red-600 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white"
              placeholder="(555) 123-4567"
            />
            {parentPhone && (
              <a
                href={`tel:${parentPhone}`}
                className="text-sm text-[#7c1d1d] dark:text-red-400 hover:underline mt-1 inline-block"
              >
                📞 Call
              </a>
            )}
          </div>

          <h4 className="font-semibold text-gray-700 dark:text-gray-300 border-b dark:border-gray-600 pb-2 mt-6">Parent/Guardian 2 (Optional)</h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Parent Name
            </label>
            <input
              type="text"
              value={parent2Name}
              onChange={(e) => setParent2Name(e.target.value)}
              disabled={!canEdit}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] dark:focus:ring-red-600 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white"
              placeholder="Full name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email Address
            </label>
            <input
              type="email"
              value={parent2Email}
              onChange={(e) => setParent2Email(e.target.value)}
              disabled={!canEdit}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] dark:focus:ring-red-600 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white"
              placeholder="parent@example.com"
            />
            {parent2Email && (
              <a
                href={`mailto:${parent2Email}`}
                className="text-sm text-[#7c1d1d] dark:text-red-400 hover:underline mt-1 inline-block"
              >
                📧 Send Email
              </a>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              value={parent2Phone}
              onChange={(e) => setParent2Phone(e.target.value)}
              disabled={!canEdit}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] dark:focus:ring-red-600 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white"
              placeholder="(555) 123-4567"
            />
            {parent2Phone && (
              <a
                href={`tel:${parent2Phone}`}
                className="text-sm text-[#7c1d1d] dark:text-red-400 hover:underline mt-1 inline-block"
              >
                📞 Call
              </a>
            )}
          </div>

          <h4 className="font-semibold text-gray-700 dark:text-gray-300 border-b dark:border-gray-600 pb-2 mt-6">Address</h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Home Address
            </label>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              disabled={!canEdit}
              rows={2}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] dark:focus:ring-red-600 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white resize-none"
              placeholder="Street, city, state, ZIP"
            />
          </div>

          <h4 className="font-semibold text-gray-700 dark:text-gray-300 border-b dark:border-gray-600 pb-2 mt-6">Medical</h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Known Allergies
            </label>
            <textarea
              value={allergies}
              onChange={(e) => setAllergies(e.target.value)}
              disabled={!canEdit}
              rows={3}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] dark:focus:ring-red-600 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white resize-none"
              placeholder="e.g. Peanuts, Dairy, Gluten..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Medical Conditions
            </label>
            <textarea
              value={medicalConditions}
              onChange={(e) => setMedicalConditions(e.target.value)}
              disabled={!canEdit}
              rows={3}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 focus:ring-2 focus:ring-[#7c1d1d] dark:focus:ring-red-600 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-700 dark:bg-gray-700 dark:text-white resize-none"
              placeholder="e.g. Asthma, epilepsy, medication requirements..."
            />
          </div>
        </div>

        {!canEdit && (
          <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded-lg text-sm text-yellow-800 dark:text-yellow-200">
            ⚠️ Only administrators can edit parent information
          </div>
        )}

        <div className="flex gap-3 mt-6">
          {canEdit ? (
            <>
              <button
                onClick={handleSave}
                className="flex-1 bg-[#7c1d1d] dark:bg-red-900 text-white px-4 py-2 rounded-lg hover:bg-[#651818] dark:hover:bg-red-800 font-medium transition-colors"
              >
                Save
              </button>
              <button
                onClick={onClose}
                className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 font-medium transition-colors"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className="w-full bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 font-medium transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   FOOTER
   ========================================================= */

function Footer() {
  return (
    <footer className="text-center text-sm text-gray-500 dark:text-gray-400 py-6">
      © {new Date().getFullYear()} MAPS Sunday School Logistics
    </footer>
  );
}